import { EventEmitter, Injectable, Injector, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import _ from 'underscore';
import { isNil } from 'lodash';
import moment from 'moment-timezone';

import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { FilterTypeRangeModel } from '../../../Common/Filter/models/filter-type-range.model';
import { FilterTypeDuplicatesModel } from '../../../Common/Filter/models/filter-type-duplicates.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { FacetValueModel } from '../../../api/mentions/model/facet-value.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { DuplicatesStatusEnum } from '../../../Common/Filter/enums/duplicates-status.enum';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import { JournodexSourceModel } from '../../../Common/Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '../../../api/mentions/enum/search-criteria-sources.enum';
import { SentimentModel } from '../../Shared/models/sentiment.model';
import { MediaTypeModel } from '../../Shared/models/media-type.model';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { monitoringFiltersEnum } from '../../Shared/enums/monitoring-filters.enum';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { Mention } from '../../../Common/Mentions/models/mention';
import { PaginatorServiceInterface } from '../../../Common/Misc/services/paginator.service.interface';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { DateFacetTypesEnum, dateTypeToDateFacetMap } from '../../../api/mentions/enum/date-facet-types.enum';
import { DateFacetValueModel } from '../../../api/mentions/model/date-facet-value.model';
import { SearchStatesEnum } from '../../Shared/enums/search-states.enum';
import { SearchStateModel } from '../../Shared/models/search-state.model';
import { SearchStateErrorModel } from '../models/search-state-error.model';
import { MonitoringCriteriaItemModel } from '../../../Common/MonitorShared/model/monitoring-criteria-item.model';
import { FacetResponseModel } from '../../../api/mentions/model/facet-response.model';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { TagGroupsService } from '../../../Common/Tags/services/tag-groups.service';
import { DateFacetResponseModel } from '../../../api/mentions/model/date-facet-response.model';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { SelectedMediaTypesFilterModel } from '../../Shared/models/selected-media-types-filter.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { MonitoringSeriesTypeEnum } from '../../Shared/enums/monitoring-series-type.enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SearchError } from '../errors/search.error';
import { SearchErrorsEnum } from '../enums/search-errors.enum';
import { FilterTypeTagCheckboxModel } from '../../Shared/components/TagFilterCheckboxes/filter-type-tag-checkbox.model';
import { TagFilterCheckboxesComponent } from '../../Shared/components/TagFilterCheckboxes/tag-filter-checkboxes.component';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { AggregateDateFacetTypesEnum } from '../../../api/mentions/enum/aggregate-date-facet-types.enum';
import { ReportsSeriesMapperService } from '../../../Common/Reports/services/reports-series-mapper.service';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';
import { ProminenceFacetModel } from '../../../api/mentions/model/prominence-facet.model';
import { ProminenceFilterModel } from '../../Shared/components/ProminenceFilter/prominence-filter.model';
import { ProminenceFilterComponent } from '../../Shared/components/ProminenceFilter/prominence-filter.component';
import { InvalidFiltersError } from '../../Shared/errors/invalid-filters.error';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { SourceTypeEnum } from '../../../api/source/enums/source-type.enum';
import { MonitoringKeywordFilterComponent } from '../../Shared/components/KeywordFilter/filter-keyword.component';
import { KeywordFilterModeEnum, MonitoringKeywordFilterModel } from '../../../Monitor/Shared/models/monitoring-keyword-filter.model';
import { MonitoringAdvancedBooleanModel } from '../../../Monitor/Shared/models/monitoring-advanced-boolean.model';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import {
    EngagementChipLabelArgs,
    engagementFilterKeys,
    EngagementFilterModel,
    EngagementFilterValueModel
} from '../../../Common/Filter/models/engagement-filter.model';
import { EngagementFilterComponent } from '../../../Common/Filter/components/EngagementFilter/engagement-filter.component';
import { MediaTypeTreeFilterComponent } from '../../../Common/Filter/components/MediaTypesTreeFilter/media-type-tree-filter.component';
import { mediaTypesWithSubTypesMap, MediaTypeTreeFilterModel } from '../../../Common/Filter/models/media-type-tree-filter.model';
import { FilterSectionEnum } from '../../../Common/Filter/enums/filter-section.enum';
import { SearchMode } from '../../Shared/enums/search-mode.enum';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { IncludeExcludeEnum } from '../../../Common/Filter/enums/include-exclude.enum';
import { ProminenceFilterApiModel } from '../../../api/mentions/model/prominence-filter-api.model';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { SavedFilter } from '../../../Common/Filter/models/saved-filter.model';
import { SavedFilterTypeEnum } from '../../../Common/Filter/enums/saved-filter.enum';
import { SnippetAreaEnum } from '../../../api/mentions/enum/snippet-area.enum';
import { SavedFilterCard, SavedFilterDisplayModel } from '../../../Common/Filter/components/SavedFilterWrapper/saved-filter-type.model';
import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { TagGroupModel } from '../../../Common/Tags/models/tag-group.model';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { SourceGroup } from '../../../Common/Sources/models/source-group.model';
import { MentionSearchVarsService } from '../../../Common/Dialogs/services/mention-search-vars.service';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { uniqBy } from 'lodash';
import { SampleFilterModel } from '@Monitor/Shared/models/sample-filter.model';
import { DiscoverListsFilterModel } from '../../../Common/Filter/models/discover-lists-filter.model';
import { DiscoverListsFilterComponent } from '../../../Common/Filter/components/DiscoverListsFilter/discover-lists-filter.component';
import { DiscoverListsFilterService } from '../../../Common/Filter/services/discover-lists-filter.service';
import { BlueskySubTypeEnum } from '@Common/Basic/enums/bluesky-sub-type.enum';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { SyndicateInfoModel } from '@Monitor/Shared/models/syndicate-info.model';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';
import { AgentFacetCount, AgentFilterOption, AgentsFilterModel } from '../../Shared/components/AgentsFilter/agents-filter.model';
import { BucketResponseModel } from '../../../api/mentions/model/bucket-response.model';
import { AgentsFilterComponent } from '../../Shared/components/AgentsFilter/agents-filter.component';
import { loadAgentFilterOptions } from '../../Shared/components/AgentsFilter/agents-filter-options';

export interface FacetCollection {
    dateFacets: Map<DateFacetTypesEnum, DateFacetValueModel[]>;
    facets: Map<MentionSearchFacetTypesEnum, FacetValueModel[]>;
    prominenceFacets: ProminenceFacetModel[];
    totalResults?: number;
    /** Only on account searches offering the Agents filter */
    aiAgentFacets?: Map<number, AgentFacetCount>;
}

export type RequestedFields = MonitoringCriteriaItemModel[] | JournodexSourceModel[] | JournodexGeographyModel[];


@Injectable()
export class MentionSearchService extends MentionSearchVarsService implements OnDestroy, PaginatorServiceInterface {
    // Order based on https://agility-confluence.atlassian.net/wiki/spaces/AM/pages/666680/Mention+List+Filters
    public static readonly defaultOrderedAccountFilters: readonly monitoringFiltersEnum[] = [
        // Content Section
        monitoringFiltersEnum.keywords,
        monitoringFiltersEnum.mediaTypes,
        monitoringFiltersEnum.authors,
        monitoringFiltersEnum.outlets,
        monitoringFiltersEnum.languages,
        monitoringFiltersEnum.hashtags,

        // Location Section
        monitoringFiltersEnum.continent,
        monitoringFiltersEnum.country,
        monitoringFiltersEnum.province,
        monitoringFiltersEnum.city,
        monitoringFiltersEnum.county,

        // Analysis Section
        monitoringFiltersEnum.audience,
        monitoringFiltersEnum.sentiment,
        monitoringFiltersEnum.ave,
        monitoringFiltersEnum.prominence,
        monitoringFiltersEnum.domainAuthority,
        monitoringFiltersEnum.engagementTotal,
        monitoringFiltersEnum.engagementRate,

        // Organization Section
        monitoringFiltersEnum.aiAgent,
        monitoringFiltersEnum.tags,
        monitoringFiltersEnum.tagGroups,
        monitoringFiltersEnum.sourceGroups,
        monitoringFiltersEnum.discoverLists,
        monitoringFiltersEnum.duplicates,
    ];
    public static readonly defaultOrderedMercuryFilters: readonly monitoringFiltersEnum[] = [
        // Content Section
        monitoringFiltersEnum.keywords,
        monitoringFiltersEnum.mediaTypes,
        monitoringFiltersEnum.authors,
        monitoringFiltersEnum.outlets,
        monitoringFiltersEnum.languages,
        monitoringFiltersEnum.hashtags,

        // Location Section
        monitoringFiltersEnum.continent,
        monitoringFiltersEnum.country,
        monitoringFiltersEnum.province,
        monitoringFiltersEnum.city,
        monitoringFiltersEnum.county,

        // Analysis Section
        monitoringFiltersEnum.audience,
        monitoringFiltersEnum.sentiment,
        monitoringFiltersEnum.ave,
        monitoringFiltersEnum.domainAuthority,
        monitoringFiltersEnum.engagementTotal,
        monitoringFiltersEnum.engagementRate,

        // Organization Section
        monitoringFiltersEnum.sourceGroups,
        monitoringFiltersEnum.discoverLists,
        monitoringFiltersEnum.duplicates,
    ];

    protected subscriptions: Subscription[] = [];

    //#region Filter definitions
    // content filters
    public readonly keywords = new MonitoringKeywordFilterModel(
        monitoringFiltersEnum.keywords,
        'Monitoring.filtersLocalization.keywords',
        MonitoringKeywordFilterComponent,
        this.localizationService,
        FilterSectionEnum.CONTENT,
        true
    );

    public readonly searchedKeywords: FilterTypeCheckboxModel<IncludeExcludeModel<string>> = new FilterTypeCheckboxModel(
        monitoringFiltersEnum.searchedKeywords,
        'Discover.targeting.filter.searchedKeywords',
        [],
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT
    );

    public readonly mediaTypes = new MediaTypeTreeFilterModel<MediaTypeModel>(
        monitoringFiltersEnum.mediaTypes,
        'Monitoring.filtersLocalization.mediaTypes',
        MediaTypeTreeFilterComponent,
        this.localizationService,
        [],
        new SelectedMediaTypesFilterModel([], false),
        FilterSectionEnum.CONTENT,
        undefined,
        true
    );

    public readonly authors = new FilterTypeCheckboxModel<JournodexSourceModel>(
        monitoringFiltersEnum.authors,
        'Monitoring.filtersLocalization.authors',
        [],
        true,
        (search: string) => {
            const model = new JournodexSourceModel();
            model.entityName = search;
            model.entityType = SearchCriteriaSourcesEnum.journalist;

            return {
                label: search,
                value: model
            };
        },
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT,
        true
    );
    public readonly outlets = new FilterTypeCheckboxModel<JournodexSourceModel>(
        monitoringFiltersEnum.outlets,
        'Monitoring.filtersLocalization.outlets',
        [],
        true,
        (search: string) => {
            const model = new JournodexSourceModel();
            model.entityName = search;
            model.entityType = SearchCriteriaSourcesEnum.outlet;

            return {
                label: search,
                value: model
            };
        },
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT,
        true
    );

    public readonly languages = new FilterTypeCheckboxModel<LanguageModel>(
        monitoringFiltersEnum.languages,
        'Monitoring.filtersLocalization.languages',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.CONTENT,
        true
    );

    public readonly hashtags = new FilterTypeCheckboxModel<string>(
        monitoringFiltersEnum.hashtags,
        'Monitoring.filtersLocalization.hashtags',
        [],
        true,
        (search: string) => {
            return {
                label: search,
                value: search
            };
        },
        null,
        null,
        null,
        FilterSectionEnum.CONTENT,
        true
    );

    // location filters
    public readonly continent = new FilterTypeCheckboxModel<JournodexGeographyModel>(
        monitoringFiltersEnum.continent,
        'Monitoring.filtersLocalization.continent',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );
    public readonly country = new FilterTypeCheckboxModel<JournodexGeographyModel>(
        monitoringFiltersEnum.country,
        'Common.country',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );
    public readonly province = new FilterTypeCheckboxModel<JournodexGeographyModel>(
        monitoringFiltersEnum.province,
        'Common.province',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );
    public readonly city = new FilterTypeCheckboxModel<JournodexGeographyModel>(
        monitoringFiltersEnum.city,
        'Monitoring.filtersLocalization.city',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );
    public readonly county = new FilterTypeCheckboxModel<JournodexGeographyModel>(
        monitoringFiltersEnum.county,
        'Monitoring.filtersLocalization.county',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.LOCATION,
        true
    );

    // analysis filters
    public readonly audience = new FilterTypeRangeModel(
        monitoringFiltersEnum.audience,
        'Monitoring.filtersLocalization.audience',
        0,
        Mention.maxAudienceReach,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS,
        undefined,
        false
    );

    public readonly sentiment = new FilterTypeCheckboxModel<SentimentModel>(
        monitoringFiltersEnum.sentiment,
        'Monitoring.filtersLocalization.sentiment',
        [],
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS
    );

    public readonly ave = new FilterTypeRangeModel(
        monitoringFiltersEnum.ave,
        'Monitoring.filtersLocalization.ave',
        0,
        Mention.maxAVE,
        undefined,
        true,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ANALYSIS
    );

    public readonly prominence = new ProminenceFilterModel(
        monitoringFiltersEnum.prominence,
        'Monitoring.filtersLocalization.prominence',
        ProminenceFilterComponent,
        this.localizationService,
        FilterSectionEnum.ANALYSIS
    );

    public readonly domainAuthority = new FilterTypeRangeModel(
        monitoringFiltersEnum.domainAuthority,
        'Monitoring.filtersLocalization.domainAuthority',
        1,
        Mention.maxDomainAuthorityScore,
        undefined,
        undefined,
        undefined,
        true,
        undefined,
        FilterSectionEnum.ANALYSIS
    );

    public readonly engagement = new EngagementFilterModel(
        monitoringFiltersEnum.engagementTotal,
        'Monitoring.filtersLocalization.engagements',
        EngagementFilterComponent,
        FilterSectionEnum.ANALYSIS
    );

    public readonly engagementRate = new FilterTypeRangeModel(
        monitoringFiltersEnum.engagementRate,
        'Monitoring.filtersLocalization.engagementRate',
        0,
        Mention.maxEngagementRate - 1, // If we set it to max it will end up as undefined when set
        '%',
        undefined,
        undefined,
        undefined,
        true,
        FilterSectionEnum.ANALYSIS,
        true
    );

    // Organization filters
    public readonly tags = new FilterTypeTagCheckboxModel(
        monitoringFiltersEnum.tags,
        'Monitoring.filtersLocalization.tags',
        TagFilterCheckboxesComponent,
        [],
        undefined,
        FilterSectionEnum.ORGANIZATION
    );
    public readonly tagGroups = new FilterTypeCheckboxModel<number>(
        monitoringFiltersEnum.tagGroups,
        'Monitoring.filtersLocalization.tagGroups',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly sourceGroups = new FilterTypeCheckboxModel<number>(
        monitoringFiltersEnum.sourceGroups,
        'Monitoring.filtersLocalization.sourceGroups',
        [],
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly discoverLists = new DiscoverListsFilterModel(
        monitoringFiltersEnum.discoverLists,
        'Monitoring.filtersLocalization.discoverLists',
        DiscoverListsFilterComponent,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly aiAgents = new AgentsFilterModel(
        monitoringFiltersEnum.aiAgent,
        'Monitoring.filtersLocalization.aiAgents',
        AgentsFilterComponent,
        FilterSectionEnum.ORGANIZATION,
        true
    );

    public readonly duplicates = new FilterTypeDuplicatesModel(
        monitoringFiltersEnum.duplicates,
        'Monitoring.filtersLocalization.duplicates',
        true,
        undefined,
        FilterSectionEnum.ORGANIZATION
    );

    public showSyndicates: boolean = true;

    //#endregion

    protected allFilters: FilterTypeModel[] = [
        this.keywords,
        this.searchedKeywords,
        this.authors,
        this.outlets,
        this.audience,
        this.ave,
        this.sentiment,
        this.mediaTypes,
        this.continent,
        this.country,
        this.province,
        this.city,
        this.county,
        this.languages,
        this.aiAgents,
        this.duplicates,
        this.hashtags,
        this.tags,
        this.tagGroups,
        this.sourceGroups,
        this.prominence,
        this.domainAuthority,
        this.engagement,
        this.engagementRate,
        this.discoverLists,
        this.savedFilters
    ];

    protected orderedAccountFilters: monitoringFiltersEnum[] = MentionSearchService.defaultOrderedAccountFilters.slice();
    protected orderedMercuryFilters: monitoringFiltersEnum[] = MentionSearchService.defaultOrderedMercuryFilters.slice();

    protected dateQuery: DateQueryModel = DateQueryModel.getLast24HoursQuery();
    protected dateQuery$: BehaviorSubject<DateQueryModel> = new BehaviorSubject<DateQueryModel>(this.dateQuery);

    protected userTimezonePreference: string;

    protected requests: Subscription[] = [];

    page: number = 1;
    pageSize: number = 25;
    totalNumberOfPages: number = 1;
    pagingValidator: () => Observable<boolean>;

    sortOrder: SortOrderEnum = SortOrderEnum.descending;
    sortField: SearchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum.published;

    protected searchCriteria: MentionSearchCriteriaModel = null;
    protected searchCriteria$: BehaviorSubject<MentionSearchCriteriaModel> = new BehaviorSubject<MentionSearchCriteriaModel>(this.searchCriteria);
    protected mentionCount: number = 0;
    protected mentionCount$: BehaviorSubject<number> = new BehaviorSubject<number>(this.mentionCount);
    protected mentions: Mention[] = null;
    protected mentions$: BehaviorSubject<Mention[]> = new BehaviorSubject<Mention[]>(this.mentions);

    protected latestSearch: MentionSearchCriteriaModel = null;
    protected latestSearch$: BehaviorSubject<MentionSearchCriteriaModel> = new BehaviorSubject<MentionSearchCriteriaModel>(this.latestSearch);

    protected primaryFacets: FacetCollection = null;
    protected primaryFacets$: BehaviorSubject<FacetCollection> = new BehaviorSubject<FacetCollection>(this.primaryFacets);

    protected filteredFacets: FacetCollection = null;
    protected filteredFacets$: BehaviorSubject<FacetCollection> = new BehaviorSubject<FacetCollection>(this.filteredFacets);

    protected coverageSummary: DataPointModel[] = null;
    protected coverageSummary$: BehaviorSubject<DataPointModel[]> = new BehaviorSubject<DataPointModel[]>(this.coverageSummary);

    protected combinedQuery: MentionSearchQueryModel = null;
    protected combinedQuery$: BehaviorSubject<MentionSearchQueryModel> = new BehaviorSubject<MentionSearchQueryModel>(this.combinedQuery);

    protected mainSearchRequest: Subscription;
    protected primaryFacetRequest: Subscription;
    protected filteredFacetRequest: Subscription;

    protected mainSearchState: SearchStateModel = {type: SearchStatesEnum.none};
    protected mainSearchState$: EventEmitter<SearchStateModel> = new EventEmitter<SearchStateModel>();

    private userHasMonitoring: boolean = true;
    private userHasSocial: boolean = true;

    availableMediaTypes: MediaTypeEnum[] = [];
    requireCombinedQueryResults: boolean = false;

    tagsOptions: TagModel[] = [];
    private tagsOptionsLoaded: boolean = false;
    private pendingTagsFacets: FacetValueModel[] = null;
    tagGroupOptions: TagGroupModel[] = [];
    sourceGroupOptions: SourceGroup[] = [];
    linkedinFilterSubOptions: FilterOptionModel<MediaTypeModel>[] = [];
    twitterFilterSubOptions: FilterOptionModel<MediaTypeModel>[] = [];
    blueskyFilterSubOptions: FilterOptionModel<MediaTypeModel>[] = [];
    tvFilterSubOptions: FilterOptionModel<MediaTypeModel>[] = [];

    private userHasFullTextPermission: boolean = false;
    private userHasAgents: boolean = false;
    private aiAgentsRequest: Subscription;

    /** Set by the agent's own feed, whose sidebar already narrows it by source */
    hideAiAgentsFilter: boolean = false;

    readonly geographyTypeToRegionFiltersMap: Map<GeographyTypesEnum, FilterTypeCheckboxModel<JournodexGeographyModel>> = new Map([
        [GeographyTypesEnum.continent, this.continent],
        [GeographyTypesEnum.country, this.country],
        [GeographyTypesEnum.province, this.province],
        [GeographyTypesEnum.city, this.city],
        [GeographyTypesEnum.county, this.county]
    ]);
    private readonly syndicateNeoFacetKey = 'SYNDICATE_GROUP_FACET';
    private readonly aiAgentNeoFacetKey = 'AI_AGENT_FACET';
    private readonly aiAgentSourceBucketKey = 'AI_AGENT_SOURCE';
    private readonly syndicateImpressionsKey = 'AUDIENCE_REACH';
    private readonly syndicateCreatedAtMinKey = 'CREATED_AT_MIN';
    private readonly syndicateCreatedAtMaxKey = 'CREATED_AT_MAX';

    constructor(
        protected tagsService: TagsService,
        protected tagGroupsService: TagGroupsService,
        protected journodexService: JournodexRegionsService,
        protected mentionsApi: MentionsApi,
        protected currentUserStateService: CurrentUserStateService,
        protected reportSeriesMapperService: ReportsSeriesMapperService,
        protected sourceGroupService: SourceGroupService,
        protected localizationService: LocalizationService,
        protected currencyConversionService: CurrencyConversionService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected discoverListsFilterService: DiscoverListsFilterService,
        protected injector: Injector
    ) {
        super();
        this.setMercuryFilters();
        this.setSubscriptions();
    }

    /**
     * Destroys any open subscriptions
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions.length = 0;
        this.requests.forEach(sub => sub.unsubscribe());
        this.requests.length = 0;
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
        }
        if (this.primaryFacetRequest) {
            this.primaryFacetRequest.unsubscribe();
        }
        if (this.filteredFacetRequest) {
            this.filteredFacetRequest.unsubscribe();
        }
    }

    /**
     * Creates the requisite set of subscriptions to generate content for the filters
     */
    setSubscriptions(): void {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    if (preferences) {
                        this.userTimezonePreference = preferences.timeZone;
                    }
                }
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(
                permissions => {
                    if (permissions) {
                        this.userHasMonitoring = !!permissions.find(x => x === userPermissionsEnum.advancedMonitoring);
                        this.userHasSocial = !!permissions.find(x => x === userPermissionsEnum.socialIntelligence);
                        this.userHasFullTextPermission = !!permissions.find(x => x === userPermissionsEnum.advancedMonitoringFullText);
                    }
                }
            ),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(
                (availableMediaTypes) => {
                    if (availableMediaTypes) {
                        this.availableMediaTypes = availableMediaTypes;
                    }
                }
            ),
            this.tagsService.all$.subscribe(tags => {
                this.tagsOptions = tags;
                this.tagsOptionsLoaded = true;

                if (this.pendingTagsFacets) {
                    const facets = this.pendingTagsFacets;
                    this.pendingTagsFacets = null;
                    this.getTagsOptions(facets);
                }
            }),
            this.tagGroupsService.getTagGroupsList().subscribe(tagGroups => {
                this.tagGroupOptions = tagGroups;
            }),
            this.sourceGroupService.all$.subscribe(sourceGroup => {
                this.sourceGroupOptions = sourceGroup;
            }),
            this.savedFilterService.all$.subscribe(savedFilters => {
                this.updateSavedFilters(savedFilters);
            }),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => {
                this.userHasAgents = enabled;
            })
        );
    }

    /**
     * calls super.setfilters.. but before that.. will filter out the filter depending on permissions
     */
    protected setFilters(filters: FilterTypeModel[]): void {
        if (!this.userHasSocial) {
            filters = filters.filter(mentionFilters =>
                (mentionFilters?.id !== monitoringFiltersEnum.engagementTotal) && (mentionFilters?.id !== monitoringFiltersEnum.engagementRate)
            );
        }

        // Agents are only offered to accounts that have them. Where the search is already one agent's
        // coverage - an agent source - the filter narrows that agent to some of its sources instead
        const scopedAgentIds = this.searchCriteria?.query?.aiAgent?.include ?? [];
        this.aiAgents.setScopedAgent(scopedAgentIds.length === 1 ? +scopedAgentIds[0] : null);

        if (!this.userHasAgents || this.hideAiAgentsFilter || scopedAgentIds.length > 1) {
            filters = filters.filter(mentionFilters => mentionFilters !== this.aiAgents);
        }

        super.setFilters(filters);
    }

    /**
     * Sets the filters available for topic/search results
     */
    public setMercuryFilters(): void {
        this.mediaTypes.setAllowArchivedBroadcastFilter(false);
        const mercuryFilters = [this.savedFilters, ...this.orderedMercuryFilters.map(
            filterId => {
                return this.allFilters.find(filterType => filterType.id as monitoringFiltersEnum === filterId);
            }
        )].filter(x => !!x);
        this.setFilters(mercuryFilters);
    }

    /**
     * Sets the filters available for folder, tag, tagGroup, sourceGroup results
     */
    public setAccountFilters(sourceType: MonitoringSeriesTypeEnum): void {
        this.mediaTypes.setAllowArchivedBroadcastFilter(true);

        // used to filter out filters that are of the same type as the source
        const sourceId = this.sourceTypeToFilterType(sourceType);

        let filters =  this.orderedAccountFilters
            .map(filterId => {
                return this.allFilters.find(filterType => filterType.id as monitoringFiltersEnum === filterId);
            })
            .filter(
                filterItem => filterItem && (filterItem?.id !== sourceId)
            );

        filters.push(this.savedFilters);

        this.setFilters(filters);
    }

    private sourceTypeToFilterType(sourceType: MonitoringSeriesTypeEnum): monitoringFiltersEnum {
        switch (sourceType) {
            case MonitoringSeriesTypeEnum.tag:
                return monitoringFiltersEnum.tags;

            case MonitoringSeriesTypeEnum.tagGroup:
                return monitoringFiltersEnum.tagGroups;

            case MonitoringSeriesTypeEnum.sourceGroup:
                return monitoringFiltersEnum.sourceGroups;

            default:
                return null;
        }
    }

    /**
     * Allows for changing the available filters for mercury searches
     */
    public setOrderedMercuryFilters(types: monitoringFiltersEnum[]): void {
        this.orderedMercuryFilters = types;

        if (this.searchCriteria && !this.searchCriteria.isAccountSearch) {
            this.setMercuryFilters();
        }
    }

    /**
     * Allows for changing the available filters for account searches
     */
    public setOrderedAccountFilters(types: monitoringFiltersEnum[], sourceType = MonitoringSeriesTypeEnum.folder): void {
        this.orderedAccountFilters = types;

        if (this.searchCriteria && this.searchCriteria.isAccountSearch) {
            this.setAccountFilters(sourceType);
        }
    }

    /**
     * Resets the filters to their empty states
     */
    public clearFilters(): void {
        this.allFilters.forEach(filt => filt.clear());
        this.setFilters(this.filters);

        this.requests.forEach(sub => sub.unsubscribe());
        this.requests.length = 0;
        this.searchedKeywords.options = [];
    }

    /**
     * Clears all of the options that are not locked
     * @param filter A checkbox search filter definition
     */
    protected clearUnselectedMediaTypeOptions(filt: MediaTypeTreeFilterModel<MediaTypeModel>) {
        const oldOptions: FilterOptionModel<MediaTypeModel>[] = filt.options;
        filt.options = [];
        this.keepSelectedMediaTypeOptions(filt, oldOptions);
    }

    /**
     * Sets up the filter options from the current list of facets
     */
    protected setFilterOptions(): void {
        if (!this.primaryFacets) {
            this.clearUnselectedCheckboxOptions(this.authors);
            this.clearUnselectedCheckboxOptions(this.outlets);
            this.clearUnselectedCheckboxOptions(this.sentiment);
            this.clearUnselectedMediaTypeOptions(this.mediaTypes);
            this.clearUnselectedCheckboxOptions(this.continent);
            this.clearUnselectedCheckboxOptions(this.country);
            this.clearUnselectedCheckboxOptions(this.province);
            this.clearUnselectedCheckboxOptions(this.city);
            this.clearUnselectedCheckboxOptions(this.county);
            this.clearUnselectedCheckboxOptions(this.languages);
            this.clearUnselectedCheckboxOptions(this.hashtags);
            this.clearUnselectedCheckboxOptions(this.tags);
            this.clearUnselectedCheckboxOptions(this.tagGroups);
            this.clearUnselectedCheckboxOptions(this.sourceGroups);
            // TODO?
            // this.clearUnselectedCheckboxOptions(this.prominence);

            this.authors.loading = true;
            this.outlets.loading = true;
            this.sentiment.loading = true;
            this.mediaTypes.loading = true;
            this.continent.loading = true;
            this.country.loading = true;
            this.province.loading = true;
            this.city.loading = true;
            this.county.loading = true;
            this.languages.loading = true;
            this.hashtags.loading = true;
            this.tags.loading = true;
            this.tagGroups.loading = true;
            this.sourceGroups.loading = true;
            this.prominence.loading = true;

            return;
        }

        this.getProminenceOptions(this.prominence, this.primaryFacets.prominenceFacets);

        const mediaTypeFacetValue =  this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.mediaType);

        this.primaryFacets.facets.forEach((value: FacetValueModel[], type: MentionSearchFacetTypesEnum) => {
            switch (type) {
                case MentionSearchFacetTypesEnum.contact:
                    this.getAuthorsOptions(this.authors, value);
                    break;
                case MentionSearchFacetTypesEnum.outlet:
                    this.getOutletsOptions(value);
                    break;
                case MentionSearchFacetTypesEnum.sentiment:
                    this.getSentimentsOptions(this.sentiment, value, this.searchCriteria?.query?.sentiments);
                    break;
                case MentionSearchFacetTypesEnum.mediaType:
                    this.getMediaTypesOptions(value, this.searchCriteria);
                    break;
                case MentionSearchFacetTypesEnum.tweetType:
                    this.getTwitterSubtypeOptions(this.mediaTypes, value);
                    break;
                case MentionSearchFacetTypesEnum.linkedinType:
                    this.getLinkedinSubtypeOptions(this.mediaTypes, value, mediaTypeFacetValue);
                    break;
                case MentionSearchFacetTypesEnum.blueskyType:
                    this.getBlueskySubtypeOptions(this.mediaTypes, value, mediaTypeFacetValue);
                    break;
                case MentionSearchFacetTypesEnum.broadcastType:
                    this.getBroadcastSubtypeOptions(this.mediaTypes, value, mediaTypeFacetValue);
                    break;
                case MentionSearchFacetTypesEnum.language:
                    this.getLanguageOptions(this.languages, value);
                    break;
                case MentionSearchFacetTypesEnum.continent:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.continent);
                    break;
                case MentionSearchFacetTypesEnum.country:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.country);
                    break;
                case MentionSearchFacetTypesEnum.province:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.province);
                    break;
                case MentionSearchFacetTypesEnum.city:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.city);
                    break;
                case MentionSearchFacetTypesEnum.county:
                    this.getRegionOptionsByGeographyType(value, GeographyTypesEnum.county);
                    break;
                case MentionSearchFacetTypesEnum.tag:
                    this.getTagsOptions(value);
                    break;
                case MentionSearchFacetTypesEnum.tagGroup:
                    this.getTagGroupsOptions(value);
                    break;
                case MentionSearchFacetTypesEnum.hashtags:
                    this.getHashtagOptions(this.hashtags, value);
                    break;
            }
        });

        // reset filter loading for those filters without facet data
        this.filters.forEach(filt => filt.loading = false);

        // set Source Group options without a count
        if (this.filters.includes(this.sourceGroups)) {
            this.getSourceGroupsOptions();
        }

        if (this.filters.includes(this.aiAgents)) {
            this.aiAgents.setCounts(this.primaryFacets.aiAgentFacets ?? null);
            this.getAiAgentsOptions();
        }

        this.setFilters(this.filters);
    }

    /**
     * Populates the options for the outlets filter
     * @param facets A list of facets from search results
     */
    protected getOutletsOptions(facets: FacetValueModel[]): void {
        const oldOptions = this.outlets.options;
        this.outlets.options = facets
            .filter(val => ('' + val.value).trim().length > 0)
            .map((val) => {
                const src = new JournodexSourceModel();
                src.entityType = SearchCriteriaSourcesEnum.outlet;
                src.entityName = val.value;

                return {
                    label: val.value,
                    value: src,
                    count: val.count
                };
            });
        this.keepSelectedCheckboxOptions<JournodexSourceModel>(
            this.outlets,
            oldOptions,
            (a, b) => b.entityName === a.entityName
        );
        this.outlets.loading = false;
    }

    /**
     * Generates a list of options for a geography type
     * @param facets A list of facets from search results
     * @param type A geography type
     */
    protected getRegionOptions(facets: FacetValueModel[], type: GeographyTypesEnum): Observable<FilterOptionModel<JournodexGeographyModel>[]> {
        const ids = facets.map(facet => +facet.value);

        return this.journodexService.getRegions(ids, type).pipe(map(
            (regions: JournodexGeographyModel[]) => {
                const options: FilterOptionModel<JournodexGeographyModel>[] = [];

                facets.forEach(
                    (facet) => {
                        const region = regions.find(reg => reg.id === +facet.value);

                        if (!region) {
                            return;
                        }

                        options.push({
                            label: region.displayString,
                            value: region,
                            count: facet.count
                        });
                    }
                );

                return options;
            }
        ));
    }

    /**
     * Populates the options for the region filter
     * @param facets A list of facets from search results
     */
    getRegionOptionsByGeographyType(facets: FacetValueModel[], type: GeographyTypesEnum): void {
        const filt = this.geographyTypeToRegionFiltersMap.get(type);
        if (!facets || facets.length < 1) {
            const oldOptions = filt.options;
            filt.options = [];
            this.keepSelectedCheckboxOptions<JournodexGeographyModel>(
                filt,
                oldOptions,
                (a, b) => b.id === a.id
            );
            filt.loading = false;
            return;
        }
        this.requests.push(
            this.getRegionOptions(facets, type).subscribe({
                next: (options: FilterOptionModel<JournodexGeographyModel>[]) => {
                    const oldOptions = filt.options;
                    filt.options = options;
                    this.keepSelectedCheckboxOptions<JournodexGeographyModel>(
                        filt,
                        oldOptions,
                        (a, b) => b.id === a.id
                    );
                    filt.loading = false;
                    this.setFilters(this.filters);
                },
                error: () => filt.loading = false
            })
        );
    }

    /**
     * Populates the options for the tags filter
     * @param facets A list of facets from search results
     */
    protected getTagsOptions(facets: FacetValueModel[]) {
        // can be called before tags are loaded; it will re-trigger once tagsService.all$ emits
        if (!this.tagsOptionsLoaded) {
            this.pendingTagsFacets = facets;
            return;
        }
        const oldOptions = this.tags.options;
        const options: FilterOptionModel<number>[] = [];

        if (!facets || facets.length < 1) {
            this.tags.options = options;
            this.keepSelectedCheckboxOptions<number>(
                this.tags,
                oldOptions
            );
            this.tags.loading = false;
            return;
        }

        if (facets.length >= 100) {
            this.tags.hasMoreOptions = true;
        }

        for (const facet of facets) {
            const tag = this.tagsOptions.find(x => x.id === +facet.value);

            if (!tag) {
                continue;
            }

            options.push({
                label: tag.name,
                value: +facet.value,
                count: facet.count
            });
        }

        this.tags.options = options;
        this.keepSelectedCheckboxOptions<number>(
            this.tags,
            oldOptions
        );
        this.tags.loading = false;

        this.setFilters(this.filters);
    }

    /**
     * Populates the options for the tag groups filter
     * @param facets A list of facets from search results
     */
    protected async getTagGroupsOptions(facets: FacetValueModel[]): Promise<void> {
        const oldOptions = this.tagGroups.options;
        const options: FilterOptionModel<number>[] = [];

        if (!facets || facets.length < 1) {
            this.tagGroups.options = options;
            this.keepSelectedCheckboxOptions<number>(
                this.tagGroups,
                oldOptions
            );
            this.tagGroups.loading = false;
            return;
        }

        for (const facet of facets) {
            const tagGroup = await this.tagGroupsService.getTagGroupModelById(+facet.value);

            if (!tagGroup) {
                continue;
            }

            options.push({
                label: tagGroup.name,
                value: +facet.value,
                count: facet.count
            });
        }

        this.tagGroups.options = options;
        this.keepSelectedCheckboxOptions<number>(
            this.tagGroups,
            oldOptions
        );
        this.tagGroups.loading = false;

        this.setFilters(this.filters);
    }

    /**
     * Populates the options for the media types filter
     * @param facets A list of facets from search results
    */
    protected getMediaTypesOptions(facets: FacetValueModel[], searchCriteria: MentionSearchCriteriaModel) {
        const oldOptions = this.mediaTypes.options;
        const parentNetworkTypes = MediaTypeModel.getAllMediaTypes();

        // since mediaTypes are only defined depending what is returned from mentions.. this is just a safety incase somehow it shows up
        // if we don't have access to tiktok or bluesky, we want to hide it in mention filters
        if (this.availableMediaTypes?.length && !this.availableMediaTypes.includes(MediaTypeEnum.TIKTOK)) {
            facets = facets.filter(mediaOptions => mediaOptions.value !== MediaTypeEnum.TIKTOK);
        }

        if (this.availableMediaTypes?.length && !this.availableMediaTypes.includes(MediaTypeEnum.BLUESKY)) {
            facets = facets.filter(mediaOptions => mediaOptions.value !== MediaTypeEnum.BLUESKY);
        }

        this.mediaTypes.options = facets
            .filter(facet => {
                if (!searchCriteria || [...searchCriteria.query.includedMediaTypes, ...searchCriteria.query.excludedMediaTypes].length === 0) {
                    return true;
                }
                return [...searchCriteria.query.includedMediaTypes, ...searchCriteria.query.excludedMediaTypes].find((mediaType) => {
                    return mediaType.id === facet.value as MediaTypeEnum;
                });
            })
            .map(facet => {
                const type = parentNetworkTypes.find(mediaType => mediaType.id === facet.value as MediaTypeEnum);

                return {
                    label: type?.name,
                    value: type,
                    count: facet.count
                };
            });

        // Setting sub options for some media types
        if (
            this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.linkedinType) ||
            this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.tweetType) ||
            this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.blueskyType) ||
            this.primaryFacets.facets.get(MentionSearchFacetTypesEnum.broadcastType)
        ) {
            if (this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.LINKEDIN)?.value) {
                this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.LINKEDIN).value.children = this.linkedinFilterSubOptions;
            }

            if (this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.TWITTER)?.value) {
                this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.TWITTER).value.children = this.twitterFilterSubOptions;
            }

            if (this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.BLUESKY)?.value) {
                this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.BLUESKY).value.children = this.blueskyFilterSubOptions;
            }

            if (this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.TV)?.value) {
                this.mediaTypes.options.find(x => x.value.id === MediaTypeEnum.TV).value.children = this.tvFilterSubOptions;
            }
        }

        this.keepSelectedMediaTypeOptions(this.mediaTypes, oldOptions);

        this.mediaTypes.loading = false;
    }

    /**
     * Populates all source groups for the filter
     */
    protected getSourceGroupsOptions() {
        const oldOptions = this.sourceGroups.options;
        const options: FilterOptionModel<number>[] = [];
        this.sourceGroupService.all$.subscribe(allSourceGroups => {
            if (allSourceGroups && allSourceGroups.length > 0) {
                for (const sourceGroup of allSourceGroups) {
                    options.push({
                        label: sourceGroup.name,
                        value: sourceGroup.id,
                        count: null
                    });
                }
                this.sourceGroups.options = options;
                this.keepSelectedCheckboxOptions<number>(
                    this.sourceGroups,
                    oldOptions
                );
                this.sourceGroups.loading = false;

                this.setFilters(this.filters);
            } else {
                this.sourceGroups.loading = false;
            }
        });
    }

    /**
     * Loads the account's curation agents for the Agents filter. Loaded once and kept up to date -
     * the options are rebuilt as each list of source names arrives.
     */
    protected getAiAgentsOptions(): void {
        if (this.aiAgentsRequest) {
            return;
        }

        this.aiAgents.loading = !this.aiAgents.optionsLoaded;
        this.aiAgentsRequest = loadAgentFilterOptions(this.injector).subscribe(options => {
            // A selection read in before the agents arrived has been left out of the searches run since,
            // so once it can be resolved they're run again with it
            const resolvesPendingQuery = this.aiAgents.hasPendingQuery;

            this.aiAgents.setOptions(options);
            this.aiAgents.loading = false;

            if (resolvesPendingQuery) {
                this.applyFilters();
            } else {
                this.setFilters(this.filters);
            }
        });
        this.subscriptions.push(this.aiAgentsRequest);
    }

    public generateFilterSearchCriteria(): MentionSearchCriteriaModel {
        const criteria = new MentionSearchCriteriaModel();
        criteria.showSimilar = this.duplicates.getValue() !== DuplicatesStatusEnum.hide;
        criteria.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
        criteria.query = this.generateFilterQuery();

        return criteria;
    }

    /**
     * Creates a search query from the current filters
     */
    public generateFilterQuery(): MentionSearchQueryModel {
        const query = new MentionSearchQueryModel();

        query.dateQuery = this.dateQuery;
        query.dateQuery.timezone = this.userTimezonePreference;

        const selectedMediaTypeFilter = this.mediaTypes.getCurrentValue();

        const twitterChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.TWITTER);
        })?.children || [];

        const linkedinChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.LINKEDIN);
        })?.children || [];

        const blueskyChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.BLUESKY);
        })?.children || [];

        const tvChildren = this.mediaTypes.getCurrentValue().mediaTypes.find(media => {
            return (media.id === MediaTypeEnum.TV);
        })?.children || [];

        if (this.mediaTypes.isInclude()) {
            query.includedMediaTypes = selectedMediaTypeFilter.mediaTypes;
            query.includedTweetTypes = twitterChildren.map(media => media.value);
            query.includedLinkedinTypes = linkedinChildren.map(media => media.value);
            query.includedBlueskyTypes = blueskyChildren.map(media => media.value);
            query.includedTvTypes = tvChildren.map(media => media.value);

            query.excludedMediaTypes = [];
            query.excludedTweetTypes = [];
            query.excludedLinkedinTypes = [];
            query.excludedBlueskyTypes = [];
            query.excludedTvTypes = [];
        } else {
            query.includedMediaTypes = [];
            query.includedTweetTypes = [];
            query.includedLinkedinTypes = [];
            query.includedBlueskyTypes = [];
            query.includedTvTypes = [];

            let excludeAllX = true;
            let excludeAllLinkedIn = true;
            let excludeAllBluesky = true;
            let excludeAllTv = true;

            Object.values(TwitterSubTypeEnum).forEach(type => {
                if (!twitterChildren.some(x => x.value.childId === type)) {
                    excludeAllX = false;
                }
            });

            Object.values(LinkedinSubTypeEnum).forEach(type => {
                if (!linkedinChildren.some(x => x.value.childId === type)) {
                    excludeAllLinkedIn = false;
                }
            });

            Object.values(BlueskySubTypeEnum).forEach(type => {
                if (!blueskyChildren.some(x => x.value.childId === type)) {
                    excludeAllBluesky = false;
                }
            });

            Object.values(BroadcastSubTypeEnum).forEach(type => {
                if (!tvChildren.some(x => x.value.childId === type)) {
                    excludeAllTv = false;
                }
            });

            query.excludedMediaTypes = selectedMediaTypeFilter.mediaTypes;

            if (excludeAllX || !twitterChildren.length) {
                query.excludedTweetTypes = [];
            } else {
                query.excludedTweetTypes = twitterChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.TWITTER);
            }

            if (excludeAllLinkedIn || !linkedinChildren.length) {
                query.excludedLinkedinTypes = [];
            } else {
                query.excludedLinkedinTypes = linkedinChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.LINKEDIN);
            }

            if (excludeAllBluesky || !blueskyChildren.length) {
                query.excludedBlueskyTypes = [];
            } else {
                query.excludedBlueskyTypes = blueskyChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.BLUESKY);
            }

            if (excludeAllTv || !tvChildren.length) {
                query.excludedTvTypes = [];
            } else {
                query.excludedTvTypes = tvChildren.map(media => media.value);
                query.excludedMediaTypes = query.excludedMediaTypes.filter(media => media.id !== MediaTypeEnum.TV);
            }
        }

        if (selectedMediaTypeFilter.archivedOnly) {
            query.isArchivedMention = selectedMediaTypeFilter.archivedOnly;
        }

        if (this.keywords.getFilterMode() === KeywordFilterModeEnum.standard) {
            query.keywordGroups = this.keywords.getKeywordGroupsForSearchQuery();
            query.searchMode = SearchMode.standard;
        } else {
            if (this.keywords.getCurrentValue().expertQuery?.filter !== '') {
                query.expertBooleanQuery.filter = this.keywords.getCurrentValue().expertQuery.filter;
                query.expertBooleanQuery.searchContexts = [...this.keywords.getCurrentValue().expertQuery.searchContexts];
                query.searchMode = SearchMode.expert;
            }
            this.keywords.setFilterMode(KeywordFilterModeEnum.expert);
        }
        if (this.searchCriteria?.isRecentCoverageSearch && this.searchedKeywords.getCurrentValue().length > 0) {
            // We add includeAll here because for recent coverage, we want to show all possible matching mentions
            // Not just those with the specific AND/OR logic. This more closely matches WHY the contact was found.
            query.keywordGroups[0].or.push(
                ...this.searchedKeywords.getCurrentValue()[0].include,
                ...this.searchedKeywords.getCurrentValue()[0].includeAll
            );
            query.keywordGroups[0].not.push(...this.searchedKeywords.getCurrentValue()[0].exclude);
        }

        if (this.authors.isInclude()) {
            query.includedSources.push(...this.authors.getCurrentValue());
        } else {
            query.excludedSources.push(...this.authors.getCurrentValue());
        }

        if (this.outlets.isInclude()) {
            query.includedSources.push(...this.outlets.getCurrentValue());
        } else {
            query.excludedSources.push(...this.outlets.getCurrentValue());
        }

        query.sentiments = this.sentiment.getCurrentValue();
        query.audienceReach = this.audience.getCurrentValue();
        const range = this.ave.getCurrentValue();
        query.ave = {min: this.currencyConversionService.convertUserCurrencyToUSD(range.min),
            max: this.currencyConversionService.convertUserCurrencyToUSD(range.max), currency: 'USD'};
        if (this.languages.isInclude()) {
            query.includedLanguages = this.languages.getCurrentValue();
        } else {
            query.excludedLanguages = this.languages.getCurrentValue();
        }

        if (this.continent.isInclude()) {
            query.includedRegions.push(...this.continent.getCurrentValue());
        } else {
            query.excludedRegions.push(...this.continent.getCurrentValue());
        }

        if (this.country.isInclude()) {
            query.includedRegions.push(...this.country.getCurrentValue());
        } else {
            query.excludedRegions.push(...this.country.getCurrentValue());
        }

        if (this.province.isInclude()) {
            query.includedRegions.push(...this.province.getCurrentValue());
        } else {
            query.excludedRegions.push(...this.province.getCurrentValue());
        }

        if (this.city.isInclude()) {
            query.includedRegions.push(...this.city.getCurrentValue());
        } else {
            query.excludedRegions.push(...this.city.getCurrentValue());
        }

        if (this.county.isInclude()) {
            query.includedRegions.push(...this.county.getCurrentValue());
        } else {
            query.excludedRegions.push(...this.county.getCurrentValue());
        }

        if (this.hashtags.isInclude()) {
            query.hashtags.include.push(...this.hashtags.getCurrentValue());
        } else {
            query.hashtags.exclude.push(...this.hashtags.getCurrentValue());
        }

        if (this.prominence.isInclude()) {
            query.includedProminenceFilter = this.prominence?.getCurrentValue()?.filters || [];
            query.excludedProminenceFilter = [];
        } else {
            query.excludedProminenceFilter = this.prominence?.getCurrentValue()?.filters || [];
            query.includedProminenceFilter = [];
        }

        query.domainAuthority = this.domainAuthority.getCurrentValue();
        query.engagementFilter = {
            ...query.engagementFilter,
            ...this.engagement.getCurrentValue(),
            engagementRate: this.engagementRate.getCurrentValue(),
        };

        if (this.filters.includes(this.discoverLists)) {
            query.discoverListFilter = this.discoverLists.hasCurrentValue() ? this.discoverLists.getCurrentValue() : null;
        }

        const tagFilter = <FilterTypeTagCheckboxModel>this.filters.find(filt => filt === this.tags);
        if (tagFilter) {
            if (tagFilter.isInclude()) {
                query.tag.include = this.tags.getCurrentValue();
            } else {
                query.tag.exclude = this.tags.getCurrentValue();
            }
        }

        if (this.filters.includes(this.tagGroups)) {
            if (this.tagGroups.isInclude()) {
                query.tagGroup.include = this.tagGroups.getCurrentValue();
            } else {
                query.tagGroup.exclude = this.tagGroups.getCurrentValue();
            }
        }

        if (this.filters.includes(this.sourceGroups)) {
            if (this.sourceGroups.isInclude()) {
                query.sourceGroup.include = this.sourceGroups.getCurrentValue();
            } else {
                query.sourceGroup.exclude = this.sourceGroups.getCurrentValue();
            }
        }

        if (this.filters.includes(this.aiAgents)) {
            Object.assign(query, this.aiAgents.toQuery());
        }

        return query;
    }

    /**
     * Gets the filters from a search object and applies them to the current filters
     */
    public populateFiltersFromSearchQuery(query: MentionSearchQueryModel, includeDate: boolean = true, setFilters: boolean = false): void {
        if (includeDate) {
            if (!query.dateQuery || !query.dateQuery.type) {
                this.setDateQuery(DateQueryModel.getAllQuery());
            } else {
                this.setDateQuery(query.dateQuery);
            }
        }
        if (query.searchMode === SearchMode.expert) {
            this.keywords.setFilterMode(KeywordFilterModeEnum.expert);
            this.keywords.setValue(
                {
                    keywordGroups:[],
                    expertQuery: query.expertBooleanQuery.filter !== '' ? query.expertBooleanQuery : new MonitoringAdvancedBooleanModel()
                }
            );

        } else {
            this.keywords.setValue(
                {
                    keywordGroups: query.keywordGroups,
                    expertQuery: new MonitoringAdvancedBooleanModel()
                }
            );
        }

        const authorsIncluded = query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = query.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.journalist) {
                    authorsIncluded.push({
                        id: source.value.id,
                        entityType: SearchCriteriaSourcesEnum.journalist,
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }

        this.authors.options = [...authorsIncluded, ...authorsExcluded].map((author) => {
            return {
                label: author.entityName,
                value: author
            };
        });

        if (authorsIncluded.length) {
            this.authors.setValue(authorsIncluded);
            this.authors.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (authorsExcluded.length) {
            this.authors.setValue(authorsExcluded);
            this.authors.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        const outletsIncluded = query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        const outletsExcluded = query.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.outlet) {
                    outletsIncluded.push({
                        id: source.value.id,
                        entityType: SearchCriteriaSourcesEnum.outlet,
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }

        this.outlets.options = [...outletsIncluded, ...outletsExcluded].map((author) => {
            return {
                label: author.entityName,
                value: author
            };
        });

        if (outletsIncluded.length) {
            this.outlets.setValue(outletsIncluded);
            this.outlets.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (outletsExcluded.length) {
            this.outlets.setValue(outletsExcluded);
            this.outlets.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        // empty sentiments means all the sentiments.. so we won't want to remove the options from the facet filters
        if (query.sentiments?.length) {
            this.sentiment.options = query.sentiments.map((sentiment) => {
                return {
                    label: sentiment.name,
                    value: sentiment
                };
            });
        }
        this.sentiment.setValue(query.sentiments);

        // populate twitter parent with children
        if (query.includedTweetTypes.length > 0) {
            const twitterParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (twitterParent)  {
                twitterParent.children = query.includedTweetTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTweetTypes.length) {
            let twitterParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (!twitterParent) {
                twitterParent = new MediaTypeModel(MediaTypeEnum.TWITTER);
                query.excludedMediaTypes.push(twitterParent);
            }

            twitterParent.children = query.excludedTweetTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate linkedin parent with children
        if (query.includedLinkedinTypes.length > 0) {
            const linkedinParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (linkedinParent)  {
                linkedinParent.children = query.includedLinkedinTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedLinkedinTypes.length) {
            let linkedinParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (!linkedinParent) {
                linkedinParent = new MediaTypeModel(MediaTypeEnum.LINKEDIN);
                query.excludedMediaTypes.push(linkedinParent);
            }

            linkedinParent.children = query.excludedLinkedinTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate bluesky parent with children
        if (query.includedBlueskyTypes.length > 0) {
            const blueskyParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (blueskyParent)  {
                blueskyParent.children = query.includedBlueskyTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedBlueskyTypes.length) {
            let blueskyParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (!blueskyParent) {
                blueskyParent = new MediaTypeModel(MediaTypeEnum.BLUESKY);
                query.excludedMediaTypes.push(blueskyParent);
            }

            blueskyParent.children = query.excludedBlueskyTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate TV parent with children
        if (query.includedTvTypes.length > 0) {
            const tvParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (tvParent)  {
                tvParent.children = query.includedTvTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTvTypes.length) {
            let tvParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (!tvParent) {
                tvParent = new MediaTypeModel(MediaTypeEnum.TV);
                query.excludedMediaTypes.push(tvParent);
            }

            tvParent.children = query.excludedTvTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        if (query.includedMediaTypes?.length) {
            this.mediaTypes.setValue(new SelectedMediaTypesFilterModel(query.includedMediaTypes, query.isArchivedMention));
            this.mediaTypes.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.excludedMediaTypes?.length) {
            this.mediaTypes.setValue(new SelectedMediaTypesFilterModel(query.excludedMediaTypes, query.isArchivedMention));
            this.mediaTypes.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        this.audience.setValue(query.audienceReach);

        this.domainAuthority.setValue(query.domainAuthority);

        this.ave.setValue(query.ave);

        const languagesIncluded = query.includedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        const languagesExcluded = query.excludedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        if (languagesIncluded.length) {
            this.languages.options = languagesIncluded;
            this.languages.setValue(query.includedLanguages);
            this.languages.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (languagesExcluded.length) {
            this.languages.options = languagesExcluded;
            this.languages.setValue(query.excludedLanguages);
            this.languages.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        let rawIncludedRegions: GeographyQueryModel = query.rawIncludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawIncludedRegions) && query.includedRegions && query.includedRegions.length > 0) {
            rawIncludedRegions = MentionSearchQueryModel.extractRegions(query.includedRegions);
        }
        if (rawIncludedRegions) {
            const continentIds = rawIncludedRegions.continents;
            const countryIds = rawIncludedRegions.countries;
            const provinceIds = rawIncludedRegions.provinces;
            const cityIds = rawIncludedRegions.cities;
            const countyIds = rawIncludedRegions.counties;

            if (continentIds?.length) {
                this.populateRegionFiltersFromSearch(continentIds, GeographyTypesEnum.continent);
                this.continent.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (countryIds?.length) {
                this.populateRegionFiltersFromSearch(countryIds, GeographyTypesEnum.country);
                this.country.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (provinceIds.length) {
                this.populateRegionFiltersFromSearch(provinceIds, GeographyTypesEnum.province);
                this.province.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (cityIds?.length) {
                this.populateRegionFiltersFromSearch(cityIds, GeographyTypesEnum.city);
                this.city.setIncludeExclude(IncludeExcludeEnum.include);
            }

            if (countyIds?.length) {
                this.populateRegionFiltersFromSearch(countyIds, GeographyTypesEnum.county);
                this.county.setIncludeExclude(IncludeExcludeEnum.include);
            }
        }

        let rawExcludedRegions: GeographyQueryModel = query.rawExcludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawExcludedRegions) && query?.excludedRegions?.length > 0) {
            rawExcludedRegions = MentionSearchQueryModel.extractRegions(query.excludedRegions);
        }
        if (rawExcludedRegions) {
            const continentIds = rawExcludedRegions.continents;
            const countryIds = rawExcludedRegions.countries;
            const provinceIds = rawExcludedRegions.provinces;
            const cityIds = rawExcludedRegions.cities;
            const countyIds = rawExcludedRegions.counties;

            if (continentIds?.length) {
                this.populateRegionFiltersFromSearch(continentIds, GeographyTypesEnum.continent);
                this.continent.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (countryIds?.length) {
                this.populateRegionFiltersFromSearch(countryIds, GeographyTypesEnum.country);
                this.country.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (provinceIds.length) {
                this.populateRegionFiltersFromSearch(provinceIds, GeographyTypesEnum.province);
                this.province.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (cityIds?.length) {
                this.populateRegionFiltersFromSearch(cityIds, GeographyTypesEnum.city);
                this.city.setIncludeExclude(IncludeExcludeEnum.exclude);
            }

            if (countyIds?.length) {
                this.populateRegionFiltersFromSearch(countyIds, GeographyTypesEnum.county);
                this.county.setIncludeExclude(IncludeExcludeEnum.exclude);
            }
        }

        if (query.tag.include.length > 0) {
            this.tags.loading = true;
            this.tags.setValue(query.tag.include);
            this.getTagsOptions(query.tag.include.map((tagId) => {
                return {
                    value: '' + tagId,
                    count: null
                };
            }));
            this.tags.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.tag.exclude.length > 0) {
            this.tags.loading = true;
            this.tags.setValue(query.tag.exclude);
            this.getTagsOptions(query.tag.exclude.map((tagId) => {
                return {
                    value: '' + tagId,
                    count: null
                };
            }));
            this.tags.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.tagGroup.include.length > 0) {
            this.tagGroups.loading = true;
            this.tagGroups.setValue(query.tagGroup.include);
            this.getTagGroupsOptions(query.tagGroup.include.map((tagGroupId) => {
                return {
                    value: '' + tagGroupId,
                    count: null
                };
            }));
            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.tagGroup.exclude.length > 0) {
            this.tagGroups.loading = true;
            this.tagGroups.setValue(query.tagGroup.exclude);
            this.getTagGroupsOptions(query.tagGroup.exclude.map((tagGroupId) => {
                return {
                    value: '' + tagGroupId,
                    count: null
                };
            }));
            this.tagGroups.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (query.hashtags?.include?.length) {
            this.hashtags.loading = true;
            this.hashtags.setValue(query.hashtags.include);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.include);

            this.hashtags.options = query.hashtags.include.map(hashtag => {
                return {
                    label: hashtag,
                    value: hashtag
                };
            });
        } else if (query.hashtags?.exclude?.length) {
            this.hashtags.loading = true;
            this.hashtags.setValue(query.hashtags.exclude);
            this.hashtags.setIncludeExclude(IncludeExcludeEnum.exclude);

            this.hashtags.options = query.hashtags.exclude.map(hashtag => {
                return {
                    label: hashtag,
                    value: hashtag
                };
            });
        }

        if(query.discoverListFilter) {
            this.discoverListsFilterService.populateFilter(this.discoverLists, query.discoverListFilter).subscribe();
        }

        if (AgentsFilterModel.hasQuery(query)) {
            this.aiAgents.setFromQuery(query);
        }

        if (query.sourceGroup.include.length > 0) {
            this.sourceGroups.loading = true;
            this.sourceGroups.setValue(query.sourceGroup.include);
            this.getSourceGroupsOptions();
            this.sourceGroups.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.sourceGroup.exclude.length > 0) {
            this.sourceGroups.loading = true;
            this.sourceGroups.setValue(query.sourceGroup.exclude);
            this.getSourceGroupsOptions();
            this.sourceGroups.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        const prominenceFilter = new ProminenceFilterApiModel();

        if (query.includedProminenceFilter?.length) {
            this.prominence.loading = true;
            prominenceFilter.filters = query.includedProminenceFilter;
            this.prominence.setValue(prominenceFilter);
            this.prominence.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (query.excludedProminenceFilter?.length) {
            this.prominence.loading = true;
            prominenceFilter.filters = query.excludedProminenceFilter;
            this.prominence.setValue(prominenceFilter);
            this.prominence.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        const engagementFilterValue: EngagementFilterValueModel = EngagementFilterModel.createBlankFilter();
        engagementFilterKeys.forEach(key => {
            engagementFilterValue[key] = query.engagementFilter[key];
        });
        this.engagement.setValue(engagementFilterValue);
        this.engagementRate.setValue(query.engagementFilter.engagementRate);

        if (setFilters) {
            this.setFilters(this.allFilters);
        }
    }

    protected populateRegionFiltersFromSearch(entityIds: number[], type: GeographyTypesEnum): void {
        const regionFilter = this.geographyTypeToRegionFiltersMap.get(type);
        regionFilter.loading = true;

        const entities = entityIds.map(id => {
            const region = new JournodexGeographyModel();
            region.id = id;
            region.geographicType = type;

            return region;
        });
        regionFilter.setValue(entities);
        this.getRegionOptionsByGeographyType(entityIds.map((id) => {
            return {
                value: '' + id,
                count: null
            };
        }), type);
    }

    populateDuplicateFilter(): void {
        this.duplicates.setValue(DuplicatesStatusEnum.hide);
    }

    /**
     * Sets the duplicate filter.
     */
    setDuplicateFilter(value: DuplicatesStatusEnum): void {
        this.duplicates.setValue(value);
    }

    setShowSyndicates(value: boolean): void {
        this.showSyndicates = value;
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(search: boolean = true): void {
        this.checkForSavedFilterConflicts();
        super.applyFilters();

        this.setPageNumber(1);

        let refreshPrimaryFacets = false;

        // If the main search state is error because of filter conflicts, some filter facets would be infinite loading.
        // Thus, once users change filter to resolve filter conflicts, we want to refresh the main facets
        if (this.mainSearchState.type === SearchStatesEnum.error) {
            refreshPrimaryFacets = true;
        }

        if (search) {
            this.search(refreshPrimaryFacets, true);
        }
    }

    /**
     * Checks if there are filters that need to be re-applied after a new primary search is performed
     */
    protected hasFiltersToApply(): boolean {
        // ignores date and duplicates
        return (
            (
                this.keywords.getValue() &&
                (
                    this.keywords.getValue().keywordGroups.some(x => x.hasAnyKeywords())
                    || this.keywords.getValue().expertQuery.filter !== ''
                )
            )
            || this.searchedKeywords.getValue().length > 0
            || this.authors.getValue().length > 0
            || this.outlets.getValue().length > 0
            || this.sentiment.getValue().length > 0
            || this.mediaTypes.getValue().mediaTypes.length > 0
            || this.mediaTypes.getValue().archivedOnly
            || this.continent.getValue().length > 0
            || this.country.getValue().length > 0
            || this.province.getValue().length > 0
            || this.city.getValue().length > 0
            || this.county.getValue().length > 0
            || this.languages.getValue().length > 0
            || this.tags.getValue().length > 0
            || this.tagGroups.getValue().length > 0
            || this.ave.chips.length > 0
            || this.audience.chips.length > 0
            || this.hashtags.getValue().length > 0
            || this.sourceGroups.getValue().length > 0
            || (this.prominence.getValue() && this.prominence.getValue().filters.length > 0)
            || this.domainAuthority.chips.length > 0
            || this.engagement.hasValues()
            || this.engagementRate.chips.length > 0
            || this.discoverLists.chips.length > 0
            || (this.filters.includes(this.aiAgents) && this.aiAgents.hasValue())
            || this.savedFilters.getValue().length > 0
        );
    }

    /**
     * Gets the current date query filter
     */
    public getDateQuery(): Observable<DateQueryModel> {
        return this.dateQuery$;
    }

    /**
     * Sets the date query filter
     * @param dateQuery A date query object
     */
    public setDateQuery(dateQuery: DateQueryModel, ignoreTimezone: boolean = false): void {
        const dateQueryIsValid = dateQuery && dateQuery.type;
        if (!dateQueryIsValid) {
            dateQuery = DateQueryModel.getAllQuery();
        }
        if (this.searchCriteria && !this.searchCriteria.isAccountSearch && DateQueryModel.equals(dateQuery, DateQueryModel.getAllQuery())) {
            dateQuery = DateQueryModel.getLast24HoursQuery();
        }
        this.dateQuery = dateQuery;
        if (!ignoreTimezone) {
            this.dateQuery.timezone = this.userTimezonePreference;
        }
        this.dateQuery$.next(this.dateQuery);
    }

    //#region sortOrder interface
    public setSortOrder(sortOrder: SortOrderEnum): void {
        this.sortOrder = sortOrder;
        this.searchCriteria.sortOrder = sortOrder;
        this.searchCriteria$.next(this.searchCriteria);
    }

    public setSortField(sortField: SearchCriteriaSortFieldEnum): void {
        this.sortField = sortField;
        if (!this.searchCriteria) {
            this.searchCriteria = new MentionSearchCriteriaModel();
        }
        this.searchCriteria.sortField = sortField;
        this.searchCriteria$.next(this.searchCriteria);
    }
    //#endregion

    public setSampleFilter(sampleFilter: SampleFilterModel): void {
        this.searchCriteria.query.sampleFilter = sampleFilter;
        this.searchCriteria$.next(this.searchCriteria);
    }

    //#region Paginator interface
    public getItemCount(): Observable<number> {
        return this.mentionCount$;
    }

    public getPageCriteria(): Observable<MentionSearchCriteriaModel>  {
        return this.searchCriteria$;
    }

    public setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }
    public setPageCriteria(pageCriteria: MentionSearchCriteriaModel): void {
        this.page = pageCriteria.page;
        // check if this page is not the page outside of 10K mentions
        if (this.page <= this.totalNumberOfPages) {
            this.pageSize = pageCriteria.pageSize;
            this.setSearchCriteria(pageCriteria);
            // this has to perform a search because the paginator sends it's requests here
            this.search();
        } else {
            this.setSearchState(SearchStatesEnum.error, new SearchError('The search is over 10,000 mentions', SearchErrorsEnum.outsideApiLimit));
        }
    }

    /**
     * Registers a paging validator from the briefing component
     */
    public registerPagingValidator(validator: () => Observable<boolean>): void {
        this.pagingValidator = validator;
    }

    public canSetPageCriteria(): Observable<boolean> {
        if (this.pagingValidator) {
            return this.pagingValidator();
        }
        return of(true);
    }

    public setPageNumber(page: number): void {
        this.page = page;
        if (this.searchCriteria) {
            this.searchCriteria.page = page;
            this.searchCriteria$.next(this.searchCriteria);
        }
    }
    public setPageSize(size: number): void {
        if (this.searchCriteria) {
            this.searchCriteria.pageSize = size;
        }
        if (this.pageSize !== size) {
            this.pageSize = size;
            this.search();
        }
    }
    //#endregion

    public setMentions(mentions: Mention[]): void {
        this.mentions = mentions;
        this.mentions$.next(mentions);
    }
    public getMentions(): Observable<Mention[]> {
        return this.mentions$;
    }
    public setMentionCount(mentionsCount: number): void {
        this.mentionCount = mentionsCount;
        this.mentionCount$.next(mentionsCount);
    }
    public getMentionCount(): Observable<number> {
        return this.mentionCount$;
    }


    public getSearchCriteria(): Observable<MentionSearchCriteriaModel> {
        return this.searchCriteria$;
    }

    public setSearchCriteria(searchCriteria: MentionSearchCriteriaModel, sourceType?: MonitoringSeriesTypeEnum): void {
        this.searchCriteria = searchCriteria;
        if (this.searchCriteria) {
            this.searchCriteria.page = this.page;
            this.searchCriteria.pageSize = this.pageSize;

            this.searchCriteria.sortOrder = this.sortOrder;
            this.searchCriteria.sortField = this.sortField;
        }
        this.searchCriteria$.next(this.searchCriteria);

        if (searchCriteria && searchCriteria.isRecentCoverageSearch) {
            this.setDateQuery(DateQueryModel.getLast90DaysQuery());

            if (this.userHasMonitoring) {
                this.setFilters([this.keywords, this.searchedKeywords, this.mediaTypes, this.outlets, this.discoverLists]);
            } else {
                this.setFilters([this.keywords, this.searchedKeywords, this.mediaTypes, this.discoverLists]);
            }
        } else if (searchCriteria && !searchCriteria.isAccountSearch) {
            this.setMercuryFilters();
        } else {
            this.setAccountFilters(sourceType);
        }
    }

    public getLatestSearchCriteria(): Observable<MentionSearchCriteriaModel> {
        return this.latestSearch$;
    }
    protected setLatestSearchCriteria(searchCriteria: MentionSearchCriteriaModel): void {
        this.latestSearch = searchCriteria;
        this.latestSearch$.next(this.latestSearch);
    }

    public getLatestSearchCriteriaValue(): MentionSearchCriteriaModel {
        return this.latestSearch;
    }

    protected updateKeywords(): void {
        let keywordsFromSearchQuery: string[];
        if (this.searchCriteria.query.isExpertSearch()) {
            keywordsFromSearchQuery = this.searchCriteria.query.getSearchKeywordsFromBooleanQuery();
        } else {
            keywordsFromSearchQuery = this.searchCriteria.query.combinedIncludeKeywords;
        }

        const filterKeywords: string[] = [];

        if (this.keywords.getValue()) {
            const keywordGroups = this.keywords.getValue().keywordGroups;
            keywordGroups.forEach(keywordGroup => {
                filterKeywords.push(
                    ...keywordGroup.and,
                    ...keywordGroup.or
                );
            });
        }

        if (this.searchCriteria.isRecentCoverageSearch && this.searchedKeywords.getValue()?.length > 0) {
            const includeExclude = this.searchedKeywords.getValue()[0];
            filterKeywords.push(
                ...includeExclude.include,
                ...includeExclude.includeAll
            );
        }

        this.highlightKeywords  = [
            {
                keywords: keywordsFromSearchQuery,
                caseSensitive: this.searchCriteria.query.caseSensitive,
                class: 'agpr-highlight'
            },
            {
                keywords: filterKeywords,
                caseSensitive: this.searchCriteria.query.caseSensitive,
                class: 'agpr-highlight--filter'
            }
        ];

        this.highlightKeywords$.next(this.highlightKeywords);
    }


    setSearchState(state: SearchStatesEnum, error?: any): void {
        this.mainSearchState = {
            type: state
        };
        if (error) {
            (<SearchStateErrorModel>this.mainSearchState).error = error;
        }
        this.mainSearchState$.emit(this.mainSearchState);
    }
    public getSearchState(): Observable<SearchStateModel> {
        return this.mainSearchState$;
    }

    public clearSearch(clearSavedFilters: boolean = true): void {
        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }
        if (this.filteredFacetRequest) {
            this.filteredFacetRequest.unsubscribe();
            this.filteredFacetRequest = null;
        }
        if (this.primaryFacetRequest) {
            this.primaryFacetRequest.unsubscribe();
            this.primaryFacetRequest = null;
        }

        this.setLatestSearchCriteria(null);
        this.setMentions(null);
        this.setMentionCount(0);
        this.setPrimaryFacets(null);
        this.setFilteredFacets(null);
        this.setPageNumber(1);
        this.setCoverageSummary(null);
        this.setSearchState(SearchStatesEnum.none);
        if (clearSavedFilters) {
            this.savedFilters.clear();
        }
    }


    getMentionSearchCriteriaPayload(filters?: MentionSearchCriteriaModel): MentionSearchCriteriaModel {
        if (!this.searchCriteria) {
            this.clearSearch();
            return;
        }
        const searchCriteria = this.searchCriteria.clone();

        searchCriteria.query.keywordGroups = searchCriteria.query.keywordGroups.filter(group => group.hasAnyKeywords());
        searchCriteria.query.dateQuery = DateQueryModel.clone(this.dateQuery);
        if (!searchCriteria.query.dateQuery.timezone) {
            searchCriteria.query.dateQuery.timezone = this.userTimezonePreference;
        }
        // Date Range 'all' only exists on account searches, so we should check if the saved filters from the previous route was set to 'all',
        // And send the proper error if so
        if (this.searchCriteria && !this.searchCriteria.isAccountSearch && DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
            this.clearSearch();
            this.setSearchState(
                SearchStatesEnum.error,
                new InvalidFiltersError('Filters can not be safely applied to search', [monitoringFiltersEnum.dateRange])
            );
            return;
        }
        searchCriteria.showSimilar = this.duplicates.getValue() !== DuplicatesStatusEnum.hide;
        searchCriteria.showSyndicates = this.showSyndicates;

        const filteredFacetSearchCriteria: MentionSearchCriteriaModel = searchCriteria.clone();
        const filterQuery: MentionSearchQueryModel = filters?.query || this.generateFilterQuery();

        if (this.savedFilters.getValue().length > 0) {
            this.checkForSavedFilterConflicts();
            filteredFacetSearchCriteria.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
        } else {
            filteredFacetSearchCriteria.savedFilterIds = [];
        }

        filteredFacetSearchCriteria.filterQuery = filterQuery;
        // filterQuery sentiments need to be a subset of base query sentiments, so populate the base query
        if (filteredFacetSearchCriteria.query.sentiments.length === 0 && filterQuery.sentiments.length > 0) {
            filteredFacetSearchCriteria.query.sentiments = SentimentModel.getAll();
        }
        return filteredFacetSearchCriteria;
    }

    public search(
        refreshPrimaryFacets: boolean = false,
        refreshFilteredFacets: boolean = false,
        filters?: MentionSearchCriteriaModel,
        useFilters?: boolean
    ): Observable<MentionsSearchResponseModel> {
        if (!this.searchCriteria) {
            this.clearSearch();
            return;
        }
        const searchCriteria = this.searchCriteria.clone();
        let filteredFacetSearchCriteria: MentionSearchCriteriaModel;
        let filterQuery: MentionSearchQueryModel;
        let isFiltered: boolean = false;

        searchCriteria.query.keywordGroups = searchCriteria.query.keywordGroups.filter(group => group.hasAnyKeywords());
        searchCriteria.query.dateQuery = DateQueryModel.clone(this.dateQuery);
        if (!searchCriteria.query.dateQuery.timezone) {
            searchCriteria.query.dateQuery.timezone = this.userTimezonePreference;
        }
        // Date Range 'all' only exists on account searches, so we should check if the saved filters from the previous route was set to 'all',
        // And send the proper error if so
        if (this.searchCriteria && !this.searchCriteria.isAccountSearch && DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
            this.clearSearch();
            this.setSearchState(
                SearchStatesEnum.error,
                new InvalidFiltersError('Filters can not be safely applied to search', [monitoringFiltersEnum.dateRange])
            );
            return;
        }
        searchCriteria.showSimilar = this.duplicates.getValue() !== DuplicatesStatusEnum.hide;
        searchCriteria.showSyndicates = this.showSyndicates;
        if (useFilters) {
            isFiltered = true;
            filteredFacetSearchCriteria = searchCriteria.clone();
            filteredFacetSearchCriteria.filterQuery = filters.query;
            filteredFacetSearchCriteria.showSimilar = filters.showSimilar;
            filteredFacetSearchCriteria.savedFilterIds = filters.savedFilterIds;
        } else if (this.hasFiltersToApply()) {
            isFiltered = true;
            filteredFacetSearchCriteria = searchCriteria.clone();
            filterQuery = filters?.query || this.generateFilterQuery();

            if (this.savedFilters.getValue().length > 0) {
                this.checkForSavedFilterConflicts();
                filteredFacetSearchCriteria.savedFilterIds = this.savedFilters.getValue().map(x => x.id);
            } else {
                filteredFacetSearchCriteria.savedFilterIds = [];
            }

            filteredFacetSearchCriteria.filterQuery = filterQuery;
            // filterQuery sentiments need to be a subset of base query sentiments, so populate the base query
            if (filteredFacetSearchCriteria.query.sentiments.length === 0 && filterQuery.sentiments.length > 0) {
                filteredFacetSearchCriteria.query.sentiments = SentimentModel.getAll();
            }
        } else {
            isFiltered = false;
            filteredFacetSearchCriteria = searchCriteria;
            filteredFacetSearchCriteria.filterQuery = null;
            filteredFacetSearchCriteria.savedFilterIds = [];
        }

        this.updateKeywords();
        this.setSearchState(SearchStatesEnum.loading);

        this.setLatestSearchCriteria(filteredFacetSearchCriteria);

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }
        const mainSearchRequest: Observable<MentionsSearchResponseModel> = this.fastSearch(filteredFacetSearchCriteria);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.setSearchState(SearchStatesEnum.success);
            },
            error: (err) => {
                let type = SearchErrorsEnum.unknown;

                // @TODO: this should be replaced with parsing the backend error
                if (
                    filterQuery?.isQueryTooLong() ||
                    err.error?.details?.[0]?.code === 'COM-003' ||
                    this.isQueryTextSizeViolation(err.error)
                ) {
                    type = SearchErrorsEnum.queryTooLong;
                }

                if (err.error?.details?.[0]?.code === 'COM-002') {
                    type = SearchErrorsEnum.discoverListNotExist;
                }

                this.setSearchState(SearchStatesEnum.error, new SearchError(err.message, type));

                // filters locked by users are not compatible with the current search
                const errorDetails = err.error.details;
                const filterConflictError = errorDetails?.find(x => {
                    return x.code === 'ADVMON-003' || x.code === 'ADVMON-004' || x.code === 'ADVMON-005' || x.code === 'ADVMON-006';
                });
                if (filterConflictError) {
                    this.handleFilterConflictError(filterConflictError);
                }
            }
        });

        let filteredFacetRequest: Observable<FacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(filteredFacetSearchCriteria);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<FacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(searchCriteria);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
                this.setTotalResultsToFilter(collection.totalResults);
            });
        }

        return mainSearchRequest;
    }

    protected fastSearch(search: MentionSearchCriteriaModel): Observable<MentionsSearchResponseModel> {
        const searchWithoutFacets = search.clone();
        searchWithoutFacets.facets = [];
        searchWithoutFacets.dateFacets = [];
        const searchCriteria: SearchCriteriaModel = searchWithoutFacets.getApiEntity();

        if (this.requireCombinedQueryResults) {
            searchCriteria.requireCombinedQueryResults = this.requireCombinedQueryResults;
        }

        if (searchCriteria.query.publicationDate && !searchCriteria.query.publicationDate.timeZone) {
            searchCriteria.query.publicationDate.timeZone = this.userTimezonePreference;
        }
        if (searchCriteria.query.harvestedDate && !searchCriteria.query.harvestedDate.timeZone) {
            searchCriteria.query.harvestedDate.timeZone = this.userTimezonePreference;
        }

        let searchRequest: Observable<MentionsSearchResponseModel>;
        if (this.searchCriteria.isMonitorSearch) {
            searchRequest = this.mentionsApi.viSearchAccount(searchCriteria);
            if (searchCriteria.filterQuery || searchCriteria.savedFilterIds?.length) {
                // fetch contextual snippet as body if it has filter context
                searchCriteria.snippetArea = SnippetAreaEnum.CONTEXTUAL;
            } else {
                searchCriteria.snippetArea = SnippetAreaEnum.BODY;
            }
        } else if (this.searchCriteria.isAccountSearch) {
            searchRequest = this.mentionsApi.searchAccount(searchCriteria);
            if (searchCriteria.filterQuery || searchCriteria.savedFilterIds?.length) {
                // fetch contextual snippet as body if it has filter context
                searchCriteria.snippetArea = SnippetAreaEnum.CONTEXTUAL;
            } else {
                searchCriteria.snippetArea = SnippetAreaEnum.BODY;
            }
        } else if (this.searchCriteria.isRecentCoverageSearch) {
            searchRequest = this.mentionsApi.searchRecentCoverages(searchCriteria);
        } else {
            searchCriteria.snippetArea = SnippetAreaEnum.BODY;
            searchRequest = this.mentionsApi.searchMentions(searchCriteria);
        }

        return searchRequest.pipe(
            map(results => {
                const mentions: Mention[] = results.mentions.map(mention => new Mention(mention));
                const mentionsCount: number = results.totalResults;
                this.combinedQuery$.next(new MentionSearchQueryModel(results.combinedQuery, true));

                if (!this.showSyndicates) {
                    this.fetchSyndicateInfo(mentions);
                }
                this.setMentions(mentions);
                this.setMentionCount(mentionsCount);

                return results;
            }),
            catchError(err => {
                this.setMentions([]);
                this.setMentionCount(0);

                throw err;
            })
        );
    }

    public setTotalResultsToFilter(mentionsCount: number): void {
        this.allFilters.forEach(item => {
            item.totalResults = mentionsCount;
        });
    }

    // TODO : Should restrict the facet fields to lineup with allFilters so
    // that we only get what we need
    protected facetSearch(search: MentionSearchCriteriaModel): Observable<FacetCollection> {
        const searchWithAllTheFacets = search.clone();
        if (DateQueryModel.getTotalHoursInQuery(searchWithAllTheFacets.query.dateQuery) > 24
            && searchWithAllTheFacets.query.dateQuery.relativeTimezoneDateType !== RelativeTimezoneDateTypeEnum.LAST_24_HOURS) {
            searchWithAllTheFacets.dateFacets = [
                {
                    field: DateFacetTypesEnum.created,
                    sliceSizeInSecond: 86400, // 24 hours * 60 minutes * 60 seconds
                    aggregateFacets: [
                        AggregateDateFacetTypesEnum.SUM_AD_VALUE_USD,
                        AggregateDateFacetTypesEnum.SUM_AUDIENCE_REACH
                    ]
                }
            ];
        } else {
            searchWithAllTheFacets.dateFacets = [
                {
                    field: DateFacetTypesEnum.created,
                    sliceSizeInSecond: 3600, // 60 minutes * 60 seconds:
                    aggregateFacets: [
                        AggregateDateFacetTypesEnum.SUM_AD_VALUE_USD,
                        AggregateDateFacetTypesEnum.SUM_AUDIENCE_REACH
                    ]
                }
            ];
        }
        searchWithAllTheFacets.dateFacets[0].field = dateTypeToDateFacetMap.get(searchWithAllTheFacets.query.dateQuery?.dateType)
            || searchWithAllTheFacets.dateFacets[0].field;
        searchWithAllTheFacets.pageSize = 0;

        if (searchWithAllTheFacets.query.dateQuery && !searchWithAllTheFacets.query.dateQuery.timezone) {
            searchWithAllTheFacets.query.dateQuery.timezone = this.userTimezonePreference;
        }

        let searchRequest: Observable<MentionsSearchResponseModel>;
        if (this.searchCriteria.isMonitorSearch) {
            searchWithAllTheFacets.facets = MentionSearchCriteriaModel.accountSearchFacets;
            searchRequest = this.mentionsApi.viSearchAccount(searchWithAllTheFacets.getApiEntity());
        } else if (this.searchCriteria.isAccountSearch) {
            searchWithAllTheFacets.facets = MentionSearchCriteriaModel.accountSearchFacets;

            // Agents aren't one of the account facets, so their counts come from a term facet, broken
            // down by source the same way
            if (this.filters.includes(this.aiAgents)) {
                searchWithAllTheFacets.neoFacets = [
                    ...searchWithAllTheFacets.neoFacets,
                    {
                        key: this.aiAgentNeoFacetKey,
                        termField: NeoFacetTermFieldApiEnum.AI_AGENT_ID,
                        plainBucketRequests: [{
                            key: this.aiAgentSourceBucketKey,
                            field: NeoFacetTermFieldApiEnum.AI_AGENT_SOURCE_ID
                        }]
                    }
                ];
            }

            searchRequest = this.mentionsApi.searchAccount(searchWithAllTheFacets.getApiEntity());
        } else if (this.searchCriteria.isRecentCoverageSearch) {
            const recentCoverageSearch = search.clone();
            recentCoverageSearch.facets = MentionSearchCriteriaModel.recentCoverageFacets;
            recentCoverageSearch.dateFacets = [];
            searchRequest = this.mentionsApi.searchRecentCoverages(recentCoverageSearch.getApiEntity());
        } else {
            searchWithAllTheFacets.facets = MentionSearchCriteriaModel.mercurySearchFacets;
            searchRequest = this.mentionsApi.searchMentions(searchWithAllTheFacets.getApiEntity());
        }

        return searchRequest.pipe(map(results => {
            this.reportSeriesMapperService.mapCoverageSummary(results).subscribe(
                coverageSummary => {
                    this.setCoverageSummary(coverageSummary);
                }
            );

            return this.parseFacets(results);
        }));
    }

    private isQueryTextSizeViolation(error: any): boolean {
        const violations = Array.isArray(error) ? error : error?.details;
        return !!violations?.some(violation =>
            violation?.messageTemplate?.includes('Size.message') && /\.text$/.test(violation?.path ?? '')
        );
    }

    private async handleFilterConflictError(filterConflictError: any): Promise<void> {
        const invalidValues = filterConflictError.invalidValue;
        let type, localizedValues;
        if (invalidValues.indexOf(':') > -1) {
            type = invalidValues.split(' : ')[0];
            const values: string[] = invalidValues.split(' : ')[1]?.split(',').map(x => x.trim());
            const error = new InvalidFiltersError('Filters can not be safely applied to search', [ type ], values);
            if (type !== 'Boolean') {
                switch (type) {
                    case 'MediaType':
                        type = monitoringFiltersEnum.mediaTypes;
                        localizedValues = values.map(x => this.localizationService.transform(`MediaType.${x}`));
                        break;
                    case 'Locale':
                        type = monitoringFiltersEnum.languages;
                        localizedValues = values.map(x => this.localizationService.transform(`Language.${x}`));
                        break;
                    case 'audienceReach':
                        type = monitoringFiltersEnum.audience;
                        break;
                    case 'adValueUsd':
                        type = monitoringFiltersEnum.ave;
                        break;
                }
                error.filterTypes = [ type ];
                if (localizedValues) {
                    error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                    error.localizedInvalidValueMap.set(type, localizedValues);
                }
                this.setSearchState(SearchStatesEnum.error, error);
            } else if (/includeUnset/.test(invalidValues)) {
                type = monitoringFiltersEnum.sentiment;
                localizedValues = [];
                let invalidValue = invalidValues;
                if (invalidValue.match(/includeUnset=[a-z]{4}/)[0].slice(-4) === 'true') {
                    localizedValues.push(this.localizationService.transform('Sentiment.NOT_AVAILABLE'));
                }
                // remove includeUnset
                invalidValue = invalidValue.split(' ; ')[1];
                // get specific ranges
                invalidValue = invalidValue.split(' : ')[1].split(', ');
                invalidValue.forEach(x => {
                    switch (x) {
                        case 'min=0.3 max=1.0':
                            localizedValues.push(this.localizationService.transform('Sentiment.POSITIVE'));
                            break;
                        case 'min=-0.2 max=0.2':
                            localizedValues.push(this.localizationService.transform('Sentiment.NEUTRAL'));
                            break;
                        case 'min=-1.0 max=-0.3':
                            localizedValues.push(this.localizationService.transform('Sentiment.NEGATIVE'));
                            break;
                    }
                });
                error.filterTypes = [ type ];
                error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                error.localizedInvalidValueMap.set(type, localizedValues);
                this.setSearchState(SearchStatesEnum.error, error);
            }
        } else {
            const error = new InvalidFiltersError('Filters can not be safely applied to search', [ type ]);
            const locationError = invalidValues.match(/(?:Province|City|Continent|County|Country)=\[(?:[0-9]+(?:,\s)*)+\]/)[0];
            if (locationError) {
                const entityType: GeographyTypesEnum = locationError.split('=')[0] as GeographyTypesEnum;
                const entityIds = locationError.match(/[0-9]+/g);
                this.journodexService.getRegions(entityIds, entityType).subscribe(regions => {
                    localizedValues = regions.map(x => {
                        let name = this.localizationService.transform(x.displayString);
                        if (name.match(/[,\(]/)) {
                            name = name.split(/[,\(]/)[0];
                        }
                        return name;
                    });
                    error.filterTypes = [ entityType.toLowerCase() as monitoringFiltersEnum ];
                    error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                    error.localizedInvalidValueMap.set(entityType.toLowerCase() as monitoringFiltersEnum, localizedValues);
                    this.setSearchState(SearchStatesEnum.error, error);
                });
            }
        }
    }

    public getPrimaryFacets(): Observable<FacetCollection> {
        return this.primaryFacets$;
    }
    public getFilteredFacets(): Observable<FacetCollection> {
        return this.filteredFacets$;
    }
    public getCoverageSummary(): Observable<DataPointModel[]> {
        return this.coverageSummary$;
    }
    protected setPrimaryFacets(collection: FacetCollection): void {
        if (collection) {
            this.primaryFacets = {
                facets: new Map(collection.facets),
                dateFacets: new Map(collection.dateFacets),
                prominenceFacets: collection.prominenceFacets,
                aiAgentFacets: collection.aiAgentFacets
            };
        } else {
            this.primaryFacets = null;
        }
        this.primaryFacets$.next(this.primaryFacets);
        this.setFilterOptions();
    }

    protected setFilteredFacets(collection: FacetCollection): void {
        if (collection) {
            this.filteredFacets = {
                facets: new Map(collection.facets),
                dateFacets: new Map(collection.dateFacets),
                prominenceFacets: collection.prominenceFacets
            };
        } else {
            this.filteredFacets = null;
        }
        this.filteredFacets$.next(this.filteredFacets);
    }

    protected setCoverageSummary(coverageSummary: DataPointModel[]): void {
        this.coverageSummary = coverageSummary;
        this.coverageSummary$.next(this.coverageSummary);
    }

    protected parseFacets(results: MentionsSearchResponseModel): FacetCollection {
        const facetMap: Map<MentionSearchFacetTypesEnum, FacetValueModel[]> = new Map<MentionSearchFacetTypesEnum, FacetValueModel[]>();
        const dateFacetMap: Map<DateFacetTypesEnum, DateFacetValueModel[]> = new Map<DateFacetTypesEnum, DateFacetValueModel[]>();

        // sort facetResults alphabetically by field name (needed for handling mediaType before twitterType)
        results.facetResults.sort((facetA, facetB) => facetA.field.localeCompare(facetB.field));

        results.facetResults.forEach(
            facetResult => {
                facetResult = this.setZeroFacetValues(facetResult);

                facetMap.set(facetResult.field, facetResult.values);
            }
        );
        results.dateFacetResults.forEach(
            dateFacetResult => {
                dateFacetResult = this.setZeroDateFacetValues(dateFacetResult);

                dateFacetMap.set(dateFacetResult.field, dateFacetResult.values);
            }
        );

        return {
            facets: facetMap,
            dateFacets: dateFacetMap,
            prominenceFacets: results.prominenceFacets,
            totalResults: results.totalResults,
            aiAgentFacets: this.parseAiAgentFacets(results)
        };
    }

    /** Counts by agent, and within each by source, or undefined when they weren't asked for */
    private parseAiAgentFacets(results: MentionsSearchResponseModel): Map<number, AgentFacetCount> {
        const agentFacet = results.neoFacetResults?.find(facet => facet.key === this.aiAgentNeoFacetKey);

        if (!agentFacet) {
            return undefined;
        }

        return new Map(agentFacet.buckets
            .filter(bucket => !!bucket.termValue)
            .map(bucket => {
                // Source ids are per mention, so a mention two agents saved brings the other agent's
                // source along too - harmless, since only the agent's own sources are looked up
                const sourceBuckets = (bucket.buckets?.[this.aiAgentSourceBucketKey] as BucketResponseModel[]) ?? [];

                return [+bucket.termValue, {
                    count: bucket.count,
                    sourceCounts: new Map(sourceBuckets.map(source => [+source.termValue, source.count]))
                }];
            }));
    }

    /**
     * The backend won't return facet results that are 0, so if we requested a facet and they didn't return anything, we should add the 0
     */
    public setZeroFacetValues(facet: FacetResponseModel): FacetResponseModel {
        // No search or no query, nothing requested
        if (!this.searchCriteria || !this.searchCriteria.query) {
            return facet;
        }

        const requestedFacets = this.getRequestedFacetsFromSearch(facet.field);
        if (!requestedFacets || requestedFacets.length === 0) {
            return facet;
        }

        for (const requestedFacet of requestedFacets) {
            let foundFacet: boolean;
            switch (facet.field) {
                case MentionSearchFacetTypesEnum.outlet:
                case MentionSearchFacetTypesEnum.contact:
                    // Contact and Outlet are text search, not ID match, so we need to determine if any of the requested strings occur in
                    // any of the returned facets, if any facets contain the string, it's considered "found"
                    foundFacet = !!facet.values.find(facetValueModel => {
                        const searchValue = (requestedFacet as JournodexSourceModel).entityName.replace(/\"/g, '');

                        return facetValueModel.value.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1;
                    });
                    break;

                case MentionSearchFacetTypesEnum.continent:
                case MentionSearchFacetTypesEnum.country:
                case MentionSearchFacetTypesEnum.province:
                case MentionSearchFacetTypesEnum.city:
                case MentionSearchFacetTypesEnum.county:
                    foundFacet = !!facet.values.find(facetValueModel => {
                        return +facetValueModel.value === (requestedFacet as JournodexGeographyModel).id;
                    });
                    break;

                case MentionSearchFacetTypesEnum.tweetType:
                    foundFacet = !!facet.values.find(facetValueModel => {
                        return facetValueModel.value === (requestedFacet as any)?.childId;
                    });
                    break;

                case MentionSearchFacetTypesEnum.linkedinType:
                    foundFacet = !!facet.values.find(facetValueModel => {
                        return facetValueModel.value === (requestedFacet as any)?.childId;
                    });
                    break;

                case MentionSearchFacetTypesEnum.blueskyType:
                    foundFacet = !!facet.values.find(facetValueModel => {
                        return facetValueModel.value === (requestedFacet as any)?.childId;
                    });
                    break;

                case MentionSearchFacetTypesEnum.broadcastType:
                    foundFacet = !!facet.values.find(facetValueModel => {
                        return facetValueModel.value === `${(requestedFacet as any)?.id}_${(requestedFacet as any)?.childId}`;
                    });
                    break;

                default:
                    foundFacet = !!facet.values.find(facetValueModel => facetValueModel.value === (requestedFacet as any).id);
                    break;
            }
            if (!foundFacet) {
                let requestedFacetValue: string = '';
                switch (facet.field) {
                    case MentionSearchFacetTypesEnum.outlet:
                    case MentionSearchFacetTypesEnum.contact:
                        // Values come surrounded in \"value\", remove the escaped quotes
                        requestedFacetValue = (requestedFacet as JournodexSourceModel).entityName.replace(/\"/g, '');
                        break;

                    case MentionSearchFacetTypesEnum.country:
                    case MentionSearchFacetTypesEnum.province:
                    case MentionSearchFacetTypesEnum.city:
                        requestedFacetValue = (requestedFacet as JournodexGeographyModel).id + '';
                        break;

                    case MentionSearchFacetTypesEnum.tweetType:
                        requestedFacetValue = (requestedFacet as any)?.childId;
                        break;

                    case MentionSearchFacetTypesEnum.linkedinType:
                        requestedFacetValue = (requestedFacet as any)?.childId;
                        break;

                    case MentionSearchFacetTypesEnum.blueskyType:
                        requestedFacetValue = (requestedFacet as any)?.childId;
                        break;

                    case MentionSearchFacetTypesEnum.broadcastType:
                        requestedFacetValue = `${(requestedFacet as any)?.id}_${(requestedFacet as any)?.childId}`;
                        break;

                    default:
                        requestedFacetValue = (requestedFacet as any).id as string;
                        break;
                }

                facet.values.push(
                    {
                        value: requestedFacetValue,
                        count: 0
                    }
                );
            }
        }

        return facet;
    }

    public getRequestedFacetsFromSearch(field: MentionSearchFacetTypesEnum): RequestedFields {
        let requestedFields: RequestedFields = [];

        switch (field) {
            case MentionSearchFacetTypesEnum.mediaType:
                requestedFields = this.searchCriteria.query.includedMediaTypes;
                break;

            case MentionSearchFacetTypesEnum.tweetType:
                requestedFields = this.searchCriteria.query.includedTweetTypes;
                break;

            case MentionSearchFacetTypesEnum.linkedinType:
                requestedFields = this.searchCriteria.query.includedLinkedinTypes;
                break;

            case MentionSearchFacetTypesEnum.blueskyType:
                requestedFields = this.searchCriteria.query.includedBlueskyTypes;
                break;

            case MentionSearchFacetTypesEnum.broadcastType:
                requestedFields = this.searchCriteria.query.includedTvTypes;
                break;

            case MentionSearchFacetTypesEnum.sentiment:
                requestedFields = this.searchCriteria.query.sentiments;
                break;

            case MentionSearchFacetTypesEnum.language:
                requestedFields = this.searchCriteria.query.includedLanguages;
                break;

            case MentionSearchFacetTypesEnum.contact:
                requestedFields = this.searchCriteria.query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
                break;

            case MentionSearchFacetTypesEnum.outlet:
                requestedFields = this.searchCriteria.query.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
                break;

            case MentionSearchFacetTypesEnum.continent:
                requestedFields = this.searchCriteria.query.includedRegions.filter(region => region.geographicType === GeographyTypesEnum.continent);
                break;

            case MentionSearchFacetTypesEnum.country:
                requestedFields = this.searchCriteria.query.includedRegions.filter(region => region.geographicType === GeographyTypesEnum.country);
                break;

            case MentionSearchFacetTypesEnum.province:
                requestedFields = this.searchCriteria.query.includedRegions.filter(region => region.geographicType === GeographyTypesEnum.province);
                break;

            case MentionSearchFacetTypesEnum.city:
                requestedFields = this.searchCriteria.query.includedRegions.filter(region => region.geographicType === GeographyTypesEnum.city);
                break;

            default:
                // This shouldn't happen
                break;
        }

        return requestedFields;
    }

    /**
     * The backend won't return facet results that are 0, so if we requested a facet and they didn't return anything, we should add the 0
     */
    public setZeroDateFacetValues(dateFacet: DateFacetResponseModel): DateFacetResponseModel {
        // No search or no query, nothing requested
        if (!this.searchCriteria || !this.searchCriteria.query) {
            return dateFacet;
        }

        const dateRange = this.searchCriteria.query.dateQuery;

        if (!dateRange || DateQueryModel.equals(dateRange, DateQueryModel.getAllQuery())) {
            const start = Math.max(0, dateFacet.values.findIndex(facetValue => facetValue.count > 0));
            let end = dateFacet.values.length - 1;
            while (end > start + 1 && dateFacet.values[end].count === 0) {
                end--;
            }
            dateFacet.values = dateFacet.values.slice(start, end + 1);
        }

        return dateFacet;
    }

    public setSearchedKeywords(searchedKeywords: IncludeExcludeModel<string>): void {
        this.searchedKeywords.options = [{
            label: 'Discover.targeting.filter.searchedKeywords',
            value: searchedKeywords
        }];
    }

    public setRequireCombinedQueryResults(require: boolean): void {
        this.requireCombinedQueryResults = require;
    }

    /**
     * Re-adds locked options to MediaTypeTree filter after the options list changes
     * When user locks a filter, the corresponding option must also be locked/kept, even if option no longer exists
     * @param filter MediaTypeTreeFilter
     * @param oldOptions Previous options from the filter
     */
    protected keepSelectedMediaTypeOptions(
        filt: MediaTypeTreeFilterModel<MediaTypeModel>,
        oldOptions: FilterOptionModel<MediaTypeModel>[]
    ) {
        const values: SelectedMediaTypesFilterModel = filt.getValue();

        if (!values) {

            return;
        }

        // collect the values that no longer have a matching valid options
        const valuesNotInFilter = values.mediaTypes.filter((val, index) => {
            return !filt.options.find((opt) => val.id === opt.value.id);
        });

        // collect old options that have a matching value that is no longer valid
        const oldOptionsToAdd = oldOptions.filter((opt) => {
            const foundValue = valuesNotInFilter.find((val) => val.id === opt.value?.id);

            if (foundValue) {
                opt.value = foundValue;
            }

            return !!foundValue;
        });

        // remove stale facet count & add back locked old options
        oldOptionsToAdd.forEach((opt) => delete opt.count);
        filt.options.push(...oldOptionsToAdd);

        // A value the facets do not offer and the old options never held - a filter applied from a
        // link, before any options existed to keep. Without an option of its own the chip for it
        // simply disappears, so one is made from the value itself.
        const unmatchedValues = valuesNotInFilter.filter(
            (val) => !oldOptionsToAdd.find((opt) => opt.value?.id === val.id)
        );
        filt.options.push(...unmatchedValues.map((val) => ({ label: val.name, value: val })));

        filt.setValue(filt.getValue());
    }

    /**
     * sets whether to enable engagement sights for the search query
     * @param enabled
     */
    enableEngagementInsights(enabled: boolean) {
        this.searchCriteria.query.enableEngagementInsights = enabled;
    }

    /**
     * Special case for network type filter, populates the twitter sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     */
    protected getTwitterSubtypeOptions(
        networkFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[]
    ) {
        const oldOptions = this.mediaTypes.options;
        const twitterTypes = MediaTypeModel.getAllTwitterSubTypes();
        const twitterParent = networkFilter.options.find(x => x.value.id === MediaTypeEnum.TWITTER);
        // find twitter parent then append children
        if (twitterParent) {
            // Don't clear children when no facet values are available but clear count
            if (facets.length === 0) {
                twitterParent.value.children?.forEach(child => child.count = 0);
            } else {
                twitterParent.value.children = facets.map(facet => {
                    const type = twitterTypes.find(mediaType => mediaType.childId === facet.value as TwitterSubTypeEnum);
                    return {
                        label: type?.name,
                        value: type,
                        count: facet.count,
                    };
                });
            }
            this.twitterFilterSubOptions = twitterParent.value.children;
        }

        this.keepSelectedMediaTypeOptions(
            this.mediaTypes,
            oldOptions
        );
        networkFilter.loading = false;
    }

    /**
     * Special case for network type filter, populates the linkedIn sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     *  @param mediaTypeFacetValue
     */
    protected getLinkedinSubtypeOptions(
        mediaTypeFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[],
        mediaTypeFacetValue: FacetValueModel[]
    ) {
        this.getMediaTypesOptions(mediaTypeFacetValue, this.searchCriteria);
        const oldOptions = mediaTypeFilter.options;
        const linkedinTypes = MediaTypeModel.getAllLinkedinSubTypes();
        const linkedinParent = mediaTypeFilter.options.find(x => x.value.id === MediaTypeEnum.LINKEDIN);

        if (linkedinParent) {
            // Don't clear children when no facet values are available but clear count
            if (facets.length === 0) {
                linkedinParent.value.children?.forEach(child => child.count = 0);
            } else {
                linkedinParent.value.children = facets.map(facet => {
                    const type = linkedinTypes.find(mediaType => mediaType.childId === facet.value);
                    return {
                        label: type?.name,
                        value: type,
                        count: facet.count,
                    };
                });
            }
            this.linkedinFilterSubOptions = linkedinParent.value.children;
        }

        this.keepSelectedMediaTypeOptions(mediaTypeFilter, oldOptions);
        mediaTypeFilter.loading = false;
    }

    /**
     * Special case for network type filter, populates the bluesky sub-options
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     * @param mediaTypeFacetValue
     */
    protected getBlueskySubtypeOptions(
        mediaTypeFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[],
        mediaTypeFacetValue: FacetValueModel[]
    ) {
        this.getMediaTypesOptions(mediaTypeFacetValue, this.searchCriteria);

        const oldOptions = mediaTypeFilter.options;
        const blueskySubTypes = MediaTypeModel.getAllBlueskySubTypes();
        const blueskyParent = mediaTypeFilter.options.find(x => x.value.id === MediaTypeEnum.BLUESKY);

        if (blueskyParent) {
            // Don't clear children when no facet values are available but clear count
            if (facets.length === 0) {
                blueskyParent.value.children?.forEach(child => child.count = 0);
            } else {
                blueskyParent.value.children = facets.map(facet => {
                    const type = blueskySubTypes.find(mediaType => mediaType.childId === facet.value);
                    return {
                        label: type?.name,
                        value: type,
                        count: facet.count,
                    };
                });
            }
            this.blueskyFilterSubOptions = blueskyParent.value.children;
        }

        this.keepSelectedMediaTypeOptions(mediaTypeFilter, oldOptions);
        mediaTypeFilter.loading = false;
    }

    /**
     * Populates the TV sub-options (programs, advertisments)
     * @param mediaTypeFilter filter model
     * @param facets A list of facets from search results
     * @param mediaTypeFacetValue
     */
    protected getBroadcastSubtypeOptions(
        mediaTypeFilter: MediaTypeTreeFilterModel<MediaTypeModel>,
        facets: FacetValueModel[] = [],
        mediaTypeFacetValue: FacetValueModel[] = []
    ) {
        this.getMediaTypesOptions(mediaTypeFacetValue, this.searchCriteria);

        const oldOptions = mediaTypeFilter.options;
        const tvSubTypes = MediaTypeModel.getTvSubTypes();
        const tvParent = mediaTypeFilter.options.find(x => x.value.id === MediaTypeEnum.TV);

        if (tvParent) {
            // Don't clear children when no facet values are available but clear count
            if (facets.length === 0) {
                tvParent.value.children?.forEach(child => child.count = 0);
            } else {
                tvParent.value.children = facets.map(facet => {
                    const type = tvSubTypes.find(subType => `${MediaTypeEnum.TV}_${subType.childId}` === facet.value);
                    return type ? {
                        label: type?.name,
                        value: type,
                        count: facet.count,
                    } as FilterOptionModel<MediaTypeModel> : null;
                }).filter(x => !!x);
            }
            this.tvFilterSubOptions = tvParent.value.children;
        }

        this.keepSelectedMediaTypeOptions(mediaTypeFilter, oldOptions);
        mediaTypeFilter.loading = false;
    }

    getCombinedQuery(): Observable<MentionSearchQueryModel> {
        return this.combinedQuery$;
    }

    prepareFilterModelToSave(): SavedFilter {
        const filterToSave = new SavedFilter();
        filterToSave.type = SavedFilterTypeEnum.monitoring;
        filterToSave.query = this.generateFilterQuery();
        if (this.filters.includes(this.duplicates)) {
            filterToSave.showSimilar = this.duplicates.getCurrentValue() !== DuplicatesStatusEnum.hide;
        };
        return filterToSave;
    }

    ungroupSavedFilter(savedFilter: SavedFilter): void {
        const filterQuery = savedFilter.query;
        // if either source filter or selected saved filter is using expert search mode, all keywords must be converted to expert
        if (
            this.keywords.getFilterMode() === KeywordFilterModeEnum.expert
            || filterQuery.expertBooleanQuery?.filter.length > 0
        ) {
            const keywordFilter = this.keywords.getCurrentValue();
            const expertBooleanQuery: MonitoringAdvancedBooleanModel = new MonitoringAdvancedBooleanModel();
            expertBooleanQuery.searchContexts = this.keywords.getFilterMode() === KeywordFilterModeEnum.expert ?
                keywordFilter.expertQuery.searchContexts
                : keywordFilter.keywordGroups[0].searchContexts;

            // Saved filter expert && source filter expert
            if (filterQuery.expertBooleanQuery?.filter.length > 0) {

                if (keywordFilter.expertQuery.filter?.length > 0) {
                    expertBooleanQuery.filter = `(${keywordFilter.expertQuery.filter}) AND (${filterQuery.expertBooleanQuery.filter})`;
                } else {
                    expertBooleanQuery.filter = `(${filterQuery.expertBooleanQuery.filter})`;
                }

            } else if (filterQuery.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                // Saved filter basic && source filter expert
                if (keywordFilter.expertQuery.filter.length > 0) {
                    expertBooleanQuery.filter = `(${keywordFilter.expertQuery.filter})`;
                    // NOT only keyword groups will be converted into AND NOT (keywords)
                    // If we add AND to this case, the query will be AND AND NOT
                    if (filterQuery.hasPositiveKeywordGroups()) {
                        expertBooleanQuery.filter += ' AND ';
                    }
                    expertBooleanQuery.filter += `${expertBooleanQuery.convertBasicQueryToExpert(filterQuery.keywordGroups)}`;
                } else {
                    expertBooleanQuery.filter = expertBooleanQuery.convertBasicQueryToExpert(filterQuery.keywordGroups);
                }
            }
            // Saved filter expert && source filter basic
            if (keywordFilter.keywordGroups?.some(group => group.isAnyKeywordSet())) {
                const keywords = keywordFilter.expertQuery.convertBasicQueryToExpert(keywordFilter.keywordGroups);
                if (filterQuery.expertBooleanQuery.filter.length > 0) {
                    expertBooleanQuery.filter += ` AND ${keywords}`;
                } else {
                    expertBooleanQuery.filter = keywords;
                }
            }

            this.keywords.setFilterMode(KeywordFilterModeEnum.expert);
            this.keywords.setCurrentValue(
                {
                    keywordGroups: [],
                    expertQuery:  expertBooleanQuery
                }
            );
            // Saved filter basic && source filter basic
        } else if (filterQuery.keywordGroups?.some(group => group.isAnyKeywordSet())) {
            this.keywords.setFilterMode(KeywordFilterModeEnum.standard);
            const keywordGroups = [...this.keywords.getCurrentValue().keywordGroups, ...filterQuery.keywordGroups].map(group => group.clone());
            this.keywords.setCurrentValue(
                {
                    keywordGroups: keywordGroups,
                    expertQuery:  new MonitoringAdvancedBooleanModel()
                }
            );
        }

        const authorsIncluded = filterQuery.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = filterQuery.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (this.authors.isInclude()) {
            this.authors.options.push(...authorsIncluded.map(author => {
                return {
                    label: author.entityName,
                    value: author
                };

            }));
            this.authors.setCurrentValue(uniqBy([...this.authors.getCurrentValue(), ...authorsIncluded], 'id'));
        } else {
            this.authors.options.push(...authorsExcluded.map(author => {
                return {
                    label: author.entityName,
                    value: author
                };

            }));
            this.authors.setCurrentValue(uniqBy([...this.authors.getCurrentValue(), ...authorsExcluded], 'id'));
        }


        const outletsIncluded = filterQuery.includedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        const outletsExcluded = filterQuery.excludedSources.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        if (this.outlets.isInclude()) {
            this.outlets.options.push(...outletsIncluded.map(outlet => {
                return {
                    label: outlet.entityName,
                    value: outlet
                };

            }));
            this.outlets.setCurrentValue(uniqBy([...this.outlets.getCurrentValue(), ...outletsIncluded], 'id'));
        } else {
            this.outlets.options.push(...outletsExcluded.map(outlet => {
                return {
                    label: outlet.entityName,
                    value: outlet
                };

            }));
            this.outlets.setCurrentValue(uniqBy([...this.outlets.getCurrentValue(), ...outletsExcluded], 'id'));
        }


        const languagesIncluded = filterQuery.includedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        const languagesExcluded = filterQuery.excludedLanguages.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        if (this.languages.isInclude()) {
            this.languages.options.push(...languagesIncluded);
            this.languages.setCurrentValue(uniqBy([...this.languages.getCurrentValue(), ...filterQuery.includedLanguages], 'id'));
        } else {
            this.languages.options.push(...languagesExcluded);
            this.languages.setCurrentValue(uniqBy([...this.languages.getCurrentValue(), ...filterQuery.excludedLanguages], 'id'));
        }

        if (this.mediaTypes.isInclude()) {
            this.mediaTypes.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.mediaTypes.getCurrentValue().mediaTypes, ...filterQuery.includedMediaTypes], 'id'), false)
            );
        } else {
            this.mediaTypes.setCurrentValue(new SelectedMediaTypesFilterModel(
                uniqBy([...this.mediaTypes.getCurrentValue().mediaTypes, ...filterQuery.excludedMediaTypes], 'id'), false)
            );
        }

        const newSentiments = [];
        uniqBy([...this.sentiment.getCurrentValue(), ...filterQuery.sentiments], 'id')?.forEach(val => {
            this.sentiment.options.forEach(option => {
                if (option.value.id === val.id) {
                    newSentiments.push(option.value);
                }
            });
        });

        this.sentiment.setCurrentValue(newSentiments);

        if (!this.audience.getCurrentValue().min && !this.audience.getCurrentValue().max) {
            this.audience.setCurrentValue(filterQuery.audienceReach);
        }
        if (!this.ave.getCurrentValue().min && !this.ave.getCurrentValue().max) {
            this.ave.setCurrentValue(filterQuery.ave);
        }
        const engagementFilterValue = this.engagement.getCurrentValue();
        Object.entries(engagementFilterValue).forEach(([key, value]) => {
            if ((isNil(value.min) || isNaN(value.min)) && (isNil(value.max) || isNaN(value.max))) {
                engagementFilterValue[key] = filterQuery.engagementFilter[key];
            }
        });
        this.engagement.setCurrentValue(engagementFilterValue);

        if (!this.engagementRate.getCurrentValue().min && !this.engagementRate.getCurrentValue().max) {
            this.engagementRate.setCurrentValue(filterQuery.engagementFilter.engagementRate);
        }
        if (!this.domainAuthority.getCurrentValue().min && !this.domainAuthority.getCurrentValue().max) {
            this.domainAuthority.setCurrentValue(filterQuery.domainAuthority);
        }

        let rawIncludedRegions: GeographyQueryModel = filterQuery.rawIncludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawIncludedRegions) && filterQuery.includedRegions && filterQuery.includedRegions.length > 0) {
            rawIncludedRegions = MentionSearchQueryModel.extractRegions(filterQuery.includedRegions);
        }
        if (rawIncludedRegions) {
            const continentIds = rawIncludedRegions.continents;
            const countryIds = rawIncludedRegions.countries;
            const provinceIds = rawIncludedRegions.provinces;
            const cityIds = rawIncludedRegions.cities;
            const countyIds = rawIncludedRegions.counties;
            if (continentIds?.length) {
                this.ungroupRegionSavedFilter(continentIds, GeographyTypesEnum.continent);
            }

            if (countryIds?.length) {
                this.ungroupRegionSavedFilter(countryIds, GeographyTypesEnum.country);
            }

            if (provinceIds.length) {
                this.ungroupRegionSavedFilter(provinceIds, GeographyTypesEnum.province);
            }

            if (cityIds?.length) {
                this.ungroupRegionSavedFilter(cityIds, GeographyTypesEnum.city);
            }

            if (countyIds?.length) {
                this.ungroupRegionSavedFilter(countyIds, GeographyTypesEnum.county);
            }
        }

        let rawExcludedRegions: GeographyQueryModel = filterQuery.rawExcludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawExcludedRegions) && filterQuery?.excludedRegions?.length > 0) {
            rawExcludedRegions = MentionSearchQueryModel.extractRegions(filterQuery.excludedRegions);
        }
        if (rawExcludedRegions) {
            const continentIds = rawExcludedRegions.continents;
            const countryIds = rawExcludedRegions.countries;
            const provinceIds = rawExcludedRegions.provinces;
            const cityIds = rawExcludedRegions.cities;
            const countyIds = rawExcludedRegions.counties;

            if (continentIds?.length) {
                this.ungroupRegionSavedFilter(continentIds, GeographyTypesEnum.continent);
            }

            if (countryIds?.length) {
                this.ungroupRegionSavedFilter(countryIds, GeographyTypesEnum.country);
            }

            if (provinceIds.length) {
                this.ungroupRegionSavedFilter(provinceIds, GeographyTypesEnum.province);
            }

            if (cityIds?.length) {
                this.ungroupRegionSavedFilter(cityIds, GeographyTypesEnum.city);
            }

            if (countyIds?.length) {
                this.ungroupRegionSavedFilter(countyIds, GeographyTypesEnum.county);
            }
        }

        if (this.tags.isInclude()) {
            this.tags.setCurrentValue([...new Set([...this.tags.getCurrentValue(), ...filterQuery.tag.include])]);
        } else {
            this.tags.setCurrentValue([...new Set([...this.tags.getCurrentValue(), ...filterQuery.tag.exclude])]);
        }

        if (this.tagGroups.isInclude()) {
            this.tagGroups.setCurrentValue([...new Set([...this.tagGroups.getCurrentValue(), ...filterQuery.tagGroup.include])]);
        } else {
            this.tagGroups.setCurrentValue([...new Set([...this.tagGroups.getCurrentValue(), ...filterQuery.tagGroup.exclude])]);
        }

        if (this.hashtags.isInclude()) {
            this.hashtags.setCurrentValue([...new Set([...this.hashtags.getCurrentValue(), ...filterQuery.hashtags.include])]);
        } else  {
            this.hashtags.setCurrentValue([...new Set([...this.hashtags.getCurrentValue(), ...filterQuery.hashtags.include])]);
        }

        if (this.sourceGroups.isInclude()) {
            this.sourceGroups.setCurrentValue([...new Set([...this.sourceGroups.getCurrentValue(), ...filterQuery.sourceGroup.include])]);
        } else {
            this.sourceGroups.setCurrentValue([...new Set([...this.sourceGroups.getCurrentValue(), ...filterQuery.sourceGroup.include])]);
        }

        if (filterQuery.discoverListFilter) {
            this.discoverListsFilterService.populateFilter(this.discoverLists, filterQuery.discoverListFilter, true).subscribe();
        }

        if (AgentsFilterModel.hasQuery(filterQuery)) {
            this.aiAgents.addCurrentFromQuery(filterQuery);
        }
    }

    ungroupRegionSavedFilter(entityIds: number[], type: GeographyTypesEnum): void {
        const regionFilter = this.geographyTypeToRegionFiltersMap.get(type);
        regionFilter.loading = true;
        this.getRegionOptionsByGeographyType(entityIds.map((id) => {
            return {
                value: '' + id,
                count: null
            };
        }), type);
        const entities = regionFilter.options.filter(option => entityIds.includes(option.value.id))?.map(x => x.value);
        regionFilter.setCurrentValue([...regionFilter.getCurrentValue(), ...entities]);
    }

    constructFilterCardFromValue(val: SavedFilter): SavedFilterCard {
        const query = val.query;
        const filters: SavedFilterDisplayModel[] = [];
        if (query.searchMode === SearchMode.expert) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.keywords',
                type: monitoringFiltersEnum.keywords,
                chips: [{
                    label: 'Expert Query',
                    tooltip: query.expertBooleanQuery?.filter,
                    labelArgs: { searchContexts: query.expertBooleanQuery?.searchContexts?.map(x => x.value) }
                }]
            });
        } else {
            const keywordFilter = new MonitoringKeywordFilterModel(null, null, null, this.localizationService);
            keywordFilter.setValue(    {
                keywordGroups: query.keywordGroups,
                expertQuery: new MonitoringAdvancedBooleanModel()
            });

            filters.push({
                filterName: 'Monitoring.filtersLocalization.keywords',
                type: monitoringFiltersEnum.keywords,
                chips: keywordFilter.chips
            });
        }

        // populate twitter parent with children
        if (query.includedTweetTypes.length > 0) {
            const twitterParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (twitterParent)  {
                twitterParent.children = query.includedTweetTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTweetTypes.length) {
            let twitterParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TWITTER);
            if (!twitterParent) {
                twitterParent = new MediaTypeModel(MediaTypeEnum.TWITTER);
                query.excludedMediaTypes.push(twitterParent);
            }

            twitterParent.children = query.excludedTweetTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate linkedin parent with children
        if (query.includedLinkedinTypes.length > 0) {
            const linkedinParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (linkedinParent)  {
                linkedinParent.children = query.includedLinkedinTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedLinkedinTypes.length) {
            let linkedinParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.LINKEDIN);
            if (!linkedinParent) {
                linkedinParent = new MediaTypeModel(MediaTypeEnum.LINKEDIN);
                query.excludedMediaTypes.push(linkedinParent);
            }

            linkedinParent.children = query.excludedLinkedinTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate bluesky parent with children
        if (query.includedBlueskyTypes.length > 0) {
            const blueskyParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (blueskyParent)  {
                blueskyParent.children = query.includedBlueskyTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedBlueskyTypes.length) {
            let blueskyParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.BLUESKY);
            if (!blueskyParent) {
                blueskyParent = new MediaTypeModel(MediaTypeEnum.BLUESKY);
                query.excludedMediaTypes.push(blueskyParent);
            }

            blueskyParent.children = query.excludedBlueskyTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        // populate tv parent with children
        if (query.includedTvTypes.length > 0) {
            const tvParent = query.includedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (tvParent)  {
                tvParent.children = query.includedTvTypes.map(type => {
                    return <FilterOptionModel>{
                        label: type.name,
                        value: type
                    };
                });
            }
        } else if (query.excludedTvTypes.length) {
            let tvParent = query.excludedMediaTypes.find(opt => opt.id === MediaTypeEnum.TV);
            if (!tvParent) {
                tvParent = new MediaTypeModel(MediaTypeEnum.TV);
                query.excludedMediaTypes.push(tvParent);
            }

            tvParent.children = query.excludedTvTypes.map(type => {
                return <FilterOptionModel>{
                    label: type.name,
                    value: type
                };
            });
        }

        if (query.includedMediaTypes?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.mediaTypes',
                type: monitoringFiltersEnum.mediaTypes,
                includeExclude: IncludeExcludeEnum.include,
                chips: query.includedMediaTypes?.map(x => this.constructSavedFilterMediaTypeChip(x))
            });
        } else if (query.excludedMediaTypes?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.mediaTypes',
                type: monitoringFiltersEnum.mediaTypes,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: query.excludedMediaTypes?.map(x => this.constructSavedFilterMediaTypeChip(x))
            });
        }

        const authorsIncluded = query.includedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);
        const authorsExcluded = query.excludedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.journalist);

        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.journalist) {
                    authorsIncluded.push({
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }

        if (authorsIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.authors',
                type: monitoringFiltersEnum.authors,
                includeExclude: IncludeExcludeEnum.include,
                chips: authorsIncluded.map(author => {
                    const chip: ChipModel = {
                        label: author.entityName
                    };
                    return chip;
                })
            });
        } else if (authorsExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.authors',
                type: monitoringFiltersEnum.authors,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: authorsExcluded.map(author => {
                    const chip: ChipModel = {
                        label: author.entityName
                    };
                    return chip;
                })
            });
        }

        const outletsIncluded = query.includedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        const outletsExcluded = query.excludedSources?.filter(source => source.entityType === SearchCriteriaSourcesEnum.outlet);
        if (query.filterSources?.length > 0) {
            query.filterSources.forEach(source => {
                if (source.type === SourceTypeEnum.outlet) {
                    outletsIncluded.push({
                        // @HACK when constructing filterSources, we add a pair of double quotes for b/e - refer to D20061 NEP-8747
                        // Here we remove them to generate correct filter
                        entityName: source.value.name.slice(1, -1)
                    } as JournodexSourceModel);
                }
            });
        }


        if (outletsIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.outlets',
                type: monitoringFiltersEnum.outlets,
                includeExclude: IncludeExcludeEnum.include,
                chips: outletsIncluded.map(x => {
                    const chip: ChipModel = {
                        label: x.entityName
                    };
                    return chip;
                })
            });
        } else if (outletsExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.outlets',
                type: monitoringFiltersEnum.outlets,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: outletsExcluded.map(x => {
                    const chip: ChipModel = {
                        label: x.entityName
                    };
                    return chip;
                })
            });
        }

        const languagesIncluded = query.includedLanguages?.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        const languagesExcluded = query.excludedLanguages?.map((language) => {
            return {
                label: language.name,
                value: language
            };
        });

        if (languagesIncluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.languages',
                type: monitoringFiltersEnum.languages,
                includeExclude: IncludeExcludeEnum.include,
                chips: languagesIncluded.map(x => {
                    return {label: x.label};
                })
            });
        } else if (languagesExcluded?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.languages',
                type: monitoringFiltersEnum.languages,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: languagesExcluded.map(x => {
                    return {label: x.label};
                })
            });
        }

        let rawIncludedRegions: GeographyQueryModel = query.rawIncludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawIncludedRegions) && query.includedRegions && query.includedRegions.length > 0) {
            rawIncludedRegions = MentionSearchQueryModel.extractRegions(query.includedRegions);
        }
        if (rawIncludedRegions) {
            const continentIds = rawIncludedRegions.continents;
            const countryIds = rawIncludedRegions.countries;
            const provinceIds = rawIncludedRegions.provinces;
            const cityIds = rawIncludedRegions.cities;
            const countyIds = rawIncludedRegions.counties;

            if (continentIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(continentIds, GeographyTypesEnum.continent).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.continent',
                    type: monitoringFiltersEnum.continent,
                    includeExclude: IncludeExcludeEnum.include,
                    chips: chipLabels
                });
            }

            if (countryIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(countryIds, GeographyTypesEnum.country).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.country',
                    type: monitoringFiltersEnum.country,
                    includeExclude: IncludeExcludeEnum.include,
                    chips: chipLabels
                });
            }

            if (provinceIds.length) {
                let chipLabels = [];
                this.getRegionLabel(provinceIds, GeographyTypesEnum.province).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.province',
                    type: monitoringFiltersEnum.province,
                    includeExclude: IncludeExcludeEnum.include,
                    chips: chipLabels
                });
            }

            if (cityIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(cityIds, GeographyTypesEnum.city).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.city',
                    type: monitoringFiltersEnum.city,
                    includeExclude: IncludeExcludeEnum.include,
                    chips: chipLabels
                });
            }

            if (countyIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(countyIds, GeographyTypesEnum.county).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.county',
                    type: monitoringFiltersEnum.county,
                    includeExclude: IncludeExcludeEnum.include,
                    chips: chipLabels
                });
            }
        }

        let rawExcludedRegions: GeographyQueryModel = query.rawExcludedRegions;
        if (MentionSearchQueryModel.isRawRegionEmpty(rawExcludedRegions) && query?.excludedRegions?.length > 0) {
            rawExcludedRegions = MentionSearchQueryModel.extractRegions(query.excludedRegions);
        }
        if (rawExcludedRegions) {
            const continentIds = rawExcludedRegions.continents;
            const countryIds = rawExcludedRegions.countries;
            const provinceIds = rawExcludedRegions.provinces;
            const cityIds = rawExcludedRegions.cities;
            const countyIds = rawExcludedRegions.counties;

            if (continentIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(continentIds, GeographyTypesEnum.continent).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.continent',
                    type: monitoringFiltersEnum.continent,
                    includeExclude: IncludeExcludeEnum.exclude,
                    chips: chipLabels
                });
            }

            if (countryIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(countryIds, GeographyTypesEnum.country).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.country',
                    type: monitoringFiltersEnum.country,
                    includeExclude: IncludeExcludeEnum.exclude,
                    chips: chipLabels
                });
            }

            if (provinceIds.length) {
                let chipLabels = [];
                this.getRegionLabel(provinceIds, GeographyTypesEnum.province).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.province',
                    type: monitoringFiltersEnum.province,
                    includeExclude: IncludeExcludeEnum.exclude,
                    chips: chipLabels
                });
            }

            if (cityIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(cityIds, GeographyTypesEnum.city).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.city',
                    type: monitoringFiltersEnum.city,
                    includeExclude: IncludeExcludeEnum.exclude,
                    chips: chipLabels
                });
            }

            if (countyIds?.length) {
                let chipLabels = [];
                this.getRegionLabel(countyIds, GeographyTypesEnum.county).subscribe(labels => {
                    chipLabels = labels.map(label => {
                        return {label: label};
                    });
                });
                filters.push({
                    filterName: 'Monitoring.filtersLocalization.county',
                    type: monitoringFiltersEnum.county,
                    includeExclude: IncludeExcludeEnum.exclude,
                    chips: chipLabels
                });
            }
        }

        if (query.audienceReach) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.audience');
            filters.push({
                filterName: 'Monitoring.filtersLocalization.audience',
                type: monitoringFiltersEnum.audience,
                chips: rangeFilter.makeChip(query.audienceReach) ?
                    [rangeFilter.makeChip(query.audienceReach)] : []
            });
        }

        if (query.sentiments?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sentiment',
                type: monitoringFiltersEnum.sentiment,
                chips: query.sentiments?.map(x => {
                    return {label: x.name};
                })
            });
        }

        if (query.ave) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.ave');
            const aveRange = {
                min: +this.currencyConversionService.convertCurrency(query.ave.min, false),
                max: +this.currencyConversionService.convertCurrency(query.ave.max, false)
            };
            filters.push({
                filterName: 'Monitoring.filtersLocalization.ave',
                type: monitoringFiltersEnum.ave,
                chips: rangeFilter.makeChip(aveRange) ?
                    [rangeFilter.makeChip(aveRange)] : []
            });

        }

        if (query.domainAuthority) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.domainAuthority');
            filters.push({
                filterName: 'Monitoring.filtersLocalization.domainAuthority',
                type: monitoringFiltersEnum.domainAuthority,
                chips: rangeFilter.makeChip(query.domainAuthority) ?
                    [rangeFilter.makeChip(query.domainAuthority)] : []
            });
        }

        if (query.tag.include.length > 0) {
            const chipLabels = query.tag.include.map(id => {
                if (this.tagsOptions.find(tag => tag.id === id)) {
                    return {label: this.tagsOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tags',
                type: monitoringFiltersEnum.tags,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.tag.exclude.length > 0) {
            const chipLabels = query.tag.exclude.map(id => {
                if (this.tagsOptions.find(tag => tag.id === id)) {
                    return {label: this.tagsOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tags',
                type: monitoringFiltersEnum.tags,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: chipLabels
            });
        }

        if (query.tagGroup.include.length > 0) {
            const chipLabels = query.tagGroup.include.map(id => {
                if (this.tagGroupOptions.find(tag => tag.id === id)) {
                    return {label: this.tagGroupOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tagGroups',
                type: monitoringFiltersEnum.tagGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.tagGroup.exclude.length > 0) {
            const chipLabels = query.tagGroup.exclude.map(id => {
                if (this.tagGroupOptions.find(tag => tag.id === id)) {
                    return {label: this.tagGroupOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.tagGroups',
                type: monitoringFiltersEnum.tagGroups,
                includeExclude: IncludeExcludeEnum.exclude,
                chips: chipLabels
            });
        }

        if (query.hashtags?.include?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.hashtags',
                type: monitoringFiltersEnum.hashtags,
                includeExclude: IncludeExcludeEnum.include,
                chips:  query.hashtags.include.map(x => {
                    return {label: x};
                })
            });

        } else if (query.hashtags?.exclude?.length) {
            filters.push({
                filterName: 'Monitoring.filtersLocalization.hashtags',
                type: monitoringFiltersEnum.hashtags,
                includeExclude: IncludeExcludeEnum.include,
                chips:  query.hashtags.exclude.map(x => {
                    return {label: x};
                })
            });
        }

        if (query.sourceGroup?.include?.length > 0) {
            const chipLabels = query.sourceGroup.include.map(id => {
                if (this.sourceGroupOptions.find(tag => tag.id === id)) {
                    return {label: this.sourceGroupOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sourceGroups',
                type: monitoringFiltersEnum.sourceGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        } else if (query.sourceGroup?.exclude?.length > 0) {
            const chipLabels = query.sourceGroup.include.map(id => {
                if (this.sourceGroupOptions.find(tag => tag.id === id)) {
                    return {label: this.sourceGroupOptions.find(tag => tag.id === id).name };
                }
            });
            filters.push({
                filterName: 'Monitoring.filtersLocalization.sourceGroups',
                type: monitoringFiltersEnum.sourceGroups,
                includeExclude: IncludeExcludeEnum.include,
                chips: chipLabels
            });
        }

        if (AgentsFilterModel.hasQuery(query)) {
            const { chips, includeExclude } = this.aiAgents.chipsForQuery(query);
            filters.push({
                filterName: 'Monitoring.filtersLocalization.aiAgents',
                type: monitoringFiltersEnum.aiAgent,
                includeExclude,
                chips
            });
        }

        if (query.engagementFilter?.engagementRate) {
            const rangeFilter = new FilterTypeRangeModel(null, 'Monitoring.filtersLocalization.engagementRate');
            rangeFilter.percentType = true;
            filters.push({
                filterName: 'Monitoring.filtersLocalization.engagementRate',
                type: monitoringFiltersEnum.engagementRate,
                chips: rangeFilter.makeChip(query.engagementFilter.engagementRate) ?
                    [rangeFilter.makeChip(query.engagementFilter.engagementRate)] : []
            });
        }

        if (query.engagementFilter) {
            const chips = Object.entries(query.engagementFilter).map(([key, value]) => {
                if (key === 'engagementRate') {
                    return null; // skip engagement rate here since it is already handled
                }
                const rangeFilter = new FilterTypeRangeModel(null, `Social.filter.engagements.${key}`);
                const chip = rangeFilter.makeChip(value);
                if (chip) {
                    (chip.labelArgs as EngagementChipLabelArgs).key = key as keyof EngagementFilterValueModel;
                }
                return chip;
            }).filter(chip => !!chip);
            filters.push({
                filterName: 'Monitoring.filtersLocalization.engagements',
                type: monitoringFiltersEnum.engagementTotal,
                chips: chips
            });
        }

        if (query.discoverListFilter) {
            this.discoverListsFilterService.constructFilterCard(query.discoverListFilter).subscribe(
                savedFilter => filters.push(savedFilter)
            );
        }

        filters.push({
            filterName: 'Monitoring.filtersLocalization.duplicates',
            type: monitoringFiltersEnum.duplicates,
            chips: val.showSimilar === false ?
                [{
                    label: 'Monitoring.filtersLocalization.duplicatesHidden'
                }] : []
        });



        //  Sort the filter card to the same order as filter list
        let positionToAdd = 0;
        this.filters.forEach(_filter => {
            if (filters.findIndex(x => x.type === _filter.id) >= 0) {
                const matchedFilter = filters.splice(filters.findIndex(x => x.type === _filter.id), 1)[0];
                matchedFilter.isCompatible = true;
                filters.splice(positionToAdd, 0, matchedFilter);
                positionToAdd++;
            }
        });

        const filterCard: SavedFilterCard = {
            label: val.name,
            value: val,
            filters: filters.filter(x => x.isCompatible && x.chips?.length) ?? [],
            inCompatibleFilters: filters.filter(x => !x.isCompatible && x.chips?.length) ?? []
        };
        return filterCard;
    }


    private getRegionLabel(regionIds: number[], type: GeographyTypesEnum): Observable<string[]> {
        return this.journodexService.getRegions(regionIds, type).pipe(
            map(regions => regions.map(x => this.localizationService.transform(x.displayString)))
        );
    }

    protected checkForSavedFilterConflicts() {
        const rangeFilterTypes = [
            monitoringFiltersEnum.ave,
            monitoringFiltersEnum.audience,
            monitoringFiltersEnum.domainAuthority,
            monitoringFiltersEnum.engagementRate,
            monitoringFiltersEnum.engagementTotal,
        ];

        const errorMessage = 'Common.filter.savedFilters.conflictFilter.chipMessage';

        let hasSavedFilterConflicts = false;
        let rangeIsValid: boolean;

        this.savedFilters.filterCards.forEach(filterCard => {
            const savedFilterQuery = filterCard.value.query;
            let conflictingValues = [];

            filterCard.filters.forEach(savedFilter => {
                savedFilter.errorMessage = null;
                const matchedSourceFilter = this.filters.find(x => x.id === savedFilter.type);

                if (!matchedSourceFilter) {
                    return;
                }

                if (rangeFilterTypes.find(x => x === matchedSourceFilter.id)) {
                    // Check if range value is valid if filter is a range filter type
                    if (matchedSourceFilter.id === monitoringFiltersEnum.ave) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(matchedSourceFilter.getCurrentValue(), savedFilterQuery.ave);
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.audience) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(matchedSourceFilter.getCurrentValue(), savedFilterQuery.audienceReach);
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.domainAuthority) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(matchedSourceFilter.getCurrentValue(), savedFilterQuery.domainAuthority);
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.engagementRate) {
                        rangeIsValid = MentionSearchQueryModel.validateRangeFilter(
                            matchedSourceFilter.getCurrentValue(),
                            savedFilterQuery.engagementFilter?.engagementRate
                        );
                    }
                    if (matchedSourceFilter.id === monitoringFiltersEnum.engagementTotal) {
                        const matchedEngagementValue = matchedSourceFilter.getCurrentValue();
                        rangeIsValid = Object.entries(savedFilterQuery.engagementFilter).reduce((pre, [key, value]) => {
                            if (key === 'engagementRate') {
                                return pre;
                            }
                            return pre && MentionSearchQueryModel.validateRangeFilter(matchedEngagementValue[key], value);
                        }, true);
                    }
                    hasSavedFilterConflicts = !rangeIsValid;
                    if (hasSavedFilterConflicts) {
                        conflictingValues = matchedSourceFilter.currentChips.map(chip => `"${this.localizationService.transform(chip.label, chip.labelArgs)}"`);
                        savedFilter.errorMessage = this.localizationService.transform(errorMessage, {filters: conflictingValues.join(', ')});
                    } else {
                        savedFilter.errorMessage = null;
                    }
                } else {
                    // Check conflicting logic if filter is a include/exclude type
                    // if both source filter and saved filter have same filter values but different include/exclude type, it is considered conflict
                    savedFilter.chips.forEach(chip => {
                        const matchedChip = matchedSourceFilter.id === monitoringFiltersEnum.languages
                            ? matchedSourceFilter.getValue().find(x => x.name.toLowerCase() === chip.label.toLocaleLowerCase())
                            : matchedSourceFilter.id === monitoringFiltersEnum.discoverLists
                                ? matchedSourceFilter.currentChips.find(x =>
                                    x.label + (x.labelArgs as any)?.list === chip.label + (chip.labelArgs as any)?.list
                                )
                                : matchedSourceFilter.currentChips.find(x => x.label.toLowerCase() === chip.label.toLowerCase());
                        if (matchedChip) {
                            conflictingValues.push(`"${this.localizationService.transform(matchedChip.label, matchedChip.labelArgs)}"`);
                        }

                        if (conflictingValues.length && matchedSourceFilter.currentChips?.length > 0
                            && (
                                (matchedSourceFilter.isInclude() && savedFilter.includeExclude === IncludeExcludeEnum.exclude)
                                || (!matchedSourceFilter.isInclude() && savedFilter.includeExclude === IncludeExcludeEnum.include)
                            )
                        ) {
                            savedFilter.errorMessage = this.localizationService.transform(errorMessage, {filters: conflictingValues.join(', ')});
                            hasSavedFilterConflicts = true;

                        }
                    });
                }
            });

            if (hasSavedFilterConflicts) {
                throw new Error('Saved Filters have conflicts');
            }

        });
    }

    constructSavedFilterMediaTypeChip(mediaType: MediaTypeModel): ChipModel {
        if (!mediaTypesWithSubTypesMap.has(mediaType.id)) {
            return { label: mediaType.name };
        } else if (mediaType.children?.length > 0) {
            let mediaTypeName = `${this.localizationService.transform(mediaType.name)}` + (mediaType?.children.length ? ': ' : '');
            if (mediaTypesWithSubTypesMap.get(mediaType.id).length === mediaType.children.length) {
                return { label: mediaTypeName + this.localizationService.transform('Social.filter.includeAll')};
            } else {
                mediaType.children.forEach((child, index) => {
                    const label = (index !== 0) ? ', ' : '';
                    mediaTypeName = mediaTypeName + label + this.localizationService.transform(child.value.name);
                });
                return { label: mediaTypeName };
            }
        } else {
            return { label: mediaType.name };
        }
    }

    setSavedFiltersFromIds(value: number[]): void {
        if (value) {
            const ids = [...new Set(value)];
            const savedFilters: SavedFilter[] = [];

            ids.forEach(id => {
                const matchFilter = this.savedFilters.options.find(x => x.value.id === id)?.value;
                if (matchFilter) {
                    savedFilters.push(matchFilter);
                }
            });

            this.savedFilters.setValue(savedFilters);
        }
    }

    // Widens a date query so syndicate searches catch related coverage published outside the user's selected window.
    // 1. This range should be user selected range + 30 days.
    // For example, if the user's selected range is last 7 days, syndication search date range is 37 days ago, with end time is set to 'now'
    // 2. If the user selects an absolute range, April 16 - 18 for example. Technically the syndication can occur both after and before the range.
    // So we need to do 16 - 30 AND 18 + 30
    // 3. If it's ALL date range, syndication search date range is 1 year
    getSyndicateMentionDateQuery(dateQuery: DateQueryModel, daysToAdd: number = 30): DateQueryModel {
        if (!dateQuery || dateQuery.type === DateQueryTypeEnum.ALL) {
            const syndicationSearchQuery = new DateQueryModel();
            syndicationSearchQuery.type = DateQueryTypeEnum.ABSOLUTE;
            syndicationSearchQuery.absoluteRange = {
                start: moment().subtract(365, 'days').toDate(),
                end: moment().toDate()
            };
            return syndicationSearchQuery;
        }

        const extended = DateQueryModel.clone(dateQuery);
        extended.dateType = undefined;
        const hoursToAdd = daysToAdd * 24;

        if (extended.type === DateQueryTypeEnum.RELATIVE) {
            extended.relativeStartOffsetH = (extended.relativeStartOffsetH ?? 0) - hoursToAdd;
            extended.relativeEndOffsetH = Math.min((extended.relativeEndOffsetH ?? 0) + hoursToAdd, 0);
            return extended;
        }

        if (extended.type === DateQueryTypeEnum.ABSOLUTE) {
            const now = moment();
            if (extended.absoluteRange) {
                extended.absoluteRange = {
                    start: moment(extended.absoluteRange.start).subtract(daysToAdd, 'days').toDate(),
                    end: moment.min(moment(extended.absoluteRange.end).add(daysToAdd, 'days'), now).toDate()
                };
            } else if (extended.absoluteDate) {
                const day = moment(extended.absoluteDate);
                extended.absoluteRange = {
                    start: day.clone().startOf('day').subtract(daysToAdd, 'days').toDate(),
                    end: moment.min(day.clone().endOf('day').add(daysToAdd, 'days'), now).toDate()
                };
                extended.absoluteDate = undefined;
            }
            return extended;
        }

        if (extended.type === DateQueryTypeEnum.RELATIVE_TIMEZONE && extended.relativeTimezoneDateType) {
            const timezone = extended.timezone ?? '';
            const start = DateQueryModel.getStartDateForRelativeTimezone(extended.relativeTimezoneDateType, timezone);
            const end = DateQueryModel.getEndDateForRelativeTimezone(extended.relativeTimezoneDateType, timezone);
            const now = moment();

            extended.type = DateQueryTypeEnum.ABSOLUTE;
            extended.absoluteRange = {
                start: start.clone().subtract(daysToAdd, 'days').toDate(),
                end: moment.min(end.clone().add(daysToAdd, 'days'), now).toDate()
            };
            extended.relativeTimezoneDateType = undefined;
            return extended;
        }

        return extended;
    }

    fetchSyndicateInfo(mentions: Mention[]): void {
        if (!mentions?.length || !this.searchCriteria) {
            return;
        }

        const syndicateIds = mentions
            .map(m => m.duplicateGroupId)
            .filter((id): id is string => !!id);

        if (!syndicateIds.length) {
            return;
        }

        const syndicateSearch = this.searchCriteria.clone();
        syndicateSearch.isAccountSearch = false;
        syndicateSearch.showSyndicates = true;
        syndicateSearch.query.syndicateIds = { include: syndicateIds, exclude: [] };
        syndicateSearch.query.keywordGroups = [];
        syndicateSearch.query.subscription = {exclude:[], include:[]}; // we never want to restrict it to a subscription
        syndicateSearch.query.searchMode = SearchMode.standard;
        syndicateSearch.query.dateQuery = this.getSyndicateMentionDateQuery(syndicateSearch.query.dateQuery);
        syndicateSearch.neoFacets = [{
            key: this.syndicateNeoFacetKey,
            termField: NeoFacetTermFieldApiEnum.SYNDICATE_GROUP,
            aggregateBucketRequests: [
                {
                    key: this.syndicateImpressionsKey,
                    field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                    function: BucketFunctionApiEnum.INDIVIDUAL_SUM
                },
                {
                    key: this.syndicateCreatedAtMinKey,
                    field: 'CREATED_AT' as NeoFacetTermFieldApiEnum,
                    function: BucketFunctionApiEnum.INDIVIDUAL_MIN
                },
                {
                    key: this.syndicateCreatedAtMaxKey,
                    field: 'CREATED_AT' as NeoFacetTermFieldApiEnum,
                    function: BucketFunctionApiEnum.INDIVIDUAL_MAX
                }
            ]
        }];
        syndicateSearch.facets = [];
        syndicateSearch.dateFacets = [];

        const apiEntity = syndicateSearch.getApiEntity();
        this.mentionsApi.searchMentions(apiEntity).subscribe((response: MentionsSearchResponseModel) => {
            const syndicateFacet = response.neoFacetResults?.find(facet => facet.key === this.syndicateNeoFacetKey);
            if (!syndicateFacet) {
                return;
            }

            const infoByGroupId = new Map<string, SyndicateInfoModel>();
            for (const bucket of syndicateFacet.buckets) {
                if (!bucket.termValue) {
                    continue;
                }
                const impressionsBucket = (bucket.buckets?.[this.syndicateImpressionsKey] as { count: number }[] | undefined)?.[0];
                const createdAtMin = parseInt((bucket.buckets?.[this.syndicateCreatedAtMinKey] as { termValue: string }[] | undefined)?.[0]?.termValue, 10);
                const createdAtMax = parseInt((bucket.buckets?.[this.syndicateCreatedAtMaxKey] as { termValue: string }[] | undefined)?.[0]?.termValue, 10);
                const diffMs = !isNaN(createdAtMin) && !isNaN(createdAtMax)
                    ? createdAtMax - createdAtMin
                    : 0;
                const diffHours = diffMs / (1000 * 60 * 60);
                const timeLabel = diffHours < 24
                    ? this.localizationService.transform('Monitor.syndicates.hoursValue', { count: Math.round(diffHours) })
                    : this.localizationService.transform('Monitor.syndicates.daysValue', { count: Math.round(diffHours / 24) });

                infoByGroupId.set(bucket.termValue, {
                    count: bucket.count,
                    timeLabel,
                    impressions: impressionsBucket?.count ?? 0
                });
            }

            for (const mention of mentions) {
                if (mention.duplicateGroupId && infoByGroupId.has(mention.duplicateGroupId)) {
                    mention.syndicateInfo = infoByGroupId.get(mention.duplicateGroupId);
                }
            }

            // we have to RE-SORT the mention list because the neofacets combines ALL the impressions of all the syndicates,
            // where the original fetch sorts only on the original mention impression (or audience reach)
            if (syndicateSearch.sortField === SearchCriteriaSortFieldEnum.audience_reach) {
                if (syndicateSearch.sortOrder === SortOrderEnum.ascending) {
                    mentions.sort((a, b) => (a?.syndicateInfo?.impressions > b?.syndicateInfo?.impressions) ? 1 : -1);
                } else {
                    mentions.sort((a, b) => (a?.syndicateInfo?.impressions < b?.syndicateInfo?.impressions) ? 1 : -1);
                }

                // we have to re-sort the mentions after
                this.setMentions(mentions);
            }
        });
    }
}

